import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import CMSPreviewPanel from '../components/cms/CMSPreviewPanel';
import { usePreviewApproval } from '../components/cms/usePreviewApproval';
import PageBuilderEditor from '../components/cms/builder/PageBuilderEditor';
import CMSPagesTable from './CMSPagesTable';
import { legacyToCanonical, canonicalToLegacy } from '../components/cms/builder/schema/legacyAdapter';
import { createBlankPageDocument } from '../components/cms/builder/schema/pageDocumentModel';
import CMSVersionHistory from '../components/cms/CMSVersionHistory';
import CMSConflictModal from '../components/cms/CMSConflictModal';

const API = process.env.REACT_APP_API_BASE_URL || `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000'}/api`;
const API_ORIGIN = API.replace(/\/api\/?$/, '');
const resolveMediaUrl = (value = '') => !value || /^(https?:)?\/\//i.test(value) || value.startsWith('data:')
  ? value
  : value.startsWith('/uploads/') ? `${API_ORIGIN}${value}` : value;

const IA_GROUPS = [
  { id: 'dashboard', label: 'Dashboard', icon: '📊', sections: ['dashboard'] },
  { id: 'pages', label: 'Pages', icon: '📄', sections: ['pages', 'homepage-sections'] },
  { id: 'page-builder', label: 'Page Builder', icon: '🎨', sections: [] },
  { id: 'global', label: 'Global Website Parts', icon: '🌐', sections: ['navigation', 'footer', 'settings'] },
  { id: 'collections', label: 'Content Collections', icon: '📦', sections: ['products', 'categories', 'brands', 'stores', 'articles', 'faqs', 'testimonials', 'banners'] },
  { id: 'media', label: 'Media Library', icon: '🖼️', sections: ['media'] },
  { id: 'seo', label: 'SEO Management', icon: '🔎', sections: ['seo'] },
  { id: 'enquiries', label: 'Enquiries', icon: '📨', sections: ['enquiries'] },
];

const resources = {
  pages: {
    label: 'Website Pages',
    icon: '📄',
    title: 'title',
    fields: [
      ['page_name', 'Website page name', 'readonly'],
      ['title', 'Page content title', 'text', true],
      ['slug', 'Page slug (enter manually)', 'manual-slug', true],
      ['route', 'Existing route to replace (example: /about)', 'route'],
      ['layout', 'Page layout', 'select', false, ['content', 'gateway', 'wave', 'custom']],
      ['show_in_navbar', 'Show in Website Navbar (Top Header Menu)', 'checkbox'],
      ['navbar_label', 'Navbar button label (optional, defaults to Page title)', 'text'],
      ['navbar_placement', 'Navbar placement', 'select', false, ['main', 'submenu']],
      ['navbar_parent_id', 'Parent menu item (if Submenu)', 'relation-navigation'],
      ['eyebrow', 'Eyebrow'],
      ['excerpt', 'Intro / excerpt', 'textarea'],
      ['content', 'Content (blank lines create paragraphs; # creates heading; - creates list)', 'textarea'],
      ['policy_content', 'Full Policy Text / Long Content (Markdown)', 'textarea'],
      ['featured_image', 'Featured image', 'image'],
      ['page_items', 'Page sections & items', 'page-items'],
      ['cta_text', 'Button text'],
      ['cta_url', 'Button URL', 'url'],
      ['promise_eyebrow', 'About Promise: Eyebrow'],
      ['promise_title', 'About Promise: Heading'],
      ['history_eyebrow', 'About History: Eyebrow'],
      ['history_title', 'About History: Heading'],
      ['history_image', 'About History: Graphic', 'image'],
      ['ecosystem_eyebrow', 'About Ecosystem: Eyebrow'],
      ['ecosystem_title', 'About Ecosystem: Heading'],
      ['leader_eyebrow', 'About Leader: Eyebrow'],
      ['leader_name', 'About Leader: Name'],
      ['leader_title', 'About Leader: Title / Designation'],
      ['leader_bio', 'About Leader: Bio', 'textarea'],
      ['leader_quote', 'About Leader: Quote'],
      ['leader_image', 'About Leader: Photo', 'image'],
      ['work_image', 'About Careers / Work Banner', 'image'],
      ['features_json', 'Custom Feature Cards (JSON)', 'textarea'],
      ['seo_title', 'SEO title'],
      ['seo_description', 'SEO description', 'textarea'],
      ['seo_keywords', 'SEO keywords'],
    ],
  },
  categories: { label: 'Categories', icon: '🗂️', title: 'name', fields: [['name', 'Category name', 'text', true], ['slug', 'Slug', 'text', true], ['parent_id', 'Parent category ID'], ['description', 'Description', 'textarea'], ['image', 'Category image', 'image'], ['alt', 'Image alt text'], ['href', 'Website URL', 'url'], ['cta_label', 'CTA label'], ['show_on_homepage', 'Show on homepage', 'checkbox'], ['seo_title', 'SEO title'], ['seo_description', 'SEO description', 'textarea']] },
  products: { label: 'Website Products', icon: '📱', title: 'name', fields: [['name', 'Product name', 'text', true], ['slug', 'Website slug'], ['sku_code', 'SKU', 'readonly'], ['brand', 'Product brand', 'readonly'], ['category', 'Product category', 'readonly'], ['website_category_id', 'Website category', 'website-category'], ['short_description', 'Short description', 'textarea'], ['description', 'Description', 'textarea'], ['regular_price', 'MRP (from catalog)', 'readonly'], ['sale_price', 'Selling price (from available variants)', 'readonly'], ['stock', 'Available stock (from inventory)', 'readonly'], ['featured_image', 'Featured image', 'image'], ['gallery', 'Gallery images', 'multi-image'], ['specifications', 'Specifications (JSON object)', 'textarea'], ['variants', 'Live variants', 'product-variants'], ['colors', 'Colors (comma separated)'], ['seo_title', 'SEO title'], ['seo_description', 'SEO description', 'textarea']] },
  banners: { label: 'Banners & Sliders', icon: '🖼️', title: 'heading', fields: [['heading', 'Heading', 'text', true], ['subheading', 'Subheading'], ['description', 'Description', 'textarea'], ['image', 'Desktop image', 'image', true], ['mobile_image', 'Mobile image', 'image'], ['video', 'Optional MP4/WebM video', 'media'], ['alt', 'Image alt text'], ['button_text', 'Button text'], ['button_url', 'Button URL', 'url'], ['placement', 'Placement', 'select', true, ['hero', 'promo', 'buy-mobile', 'buy-laptop']], ['group_key', 'Promo group key'], ['badge', 'Badge'], ['alignment', 'Content alignment', 'select', false, ['left', 'center', 'right']], ['overlay', 'Show text overlay', 'checkbox'], ['autoplay_ms', 'Slider autoplay milliseconds', 'number'], ['external', 'External link', 'checkbox'], ['new_tab', 'Open in new tab', 'checkbox']] },
  'homepage-sections': { label: 'Homepage Sections', icon: '🏠', title: 'title', fields: [['section_key', 'Unique section key', 'text', true], ['section_type', 'Section type', 'select', true, ['product', 'categories', 'brands', 'testimonials', 'stores', 'articles', 'callout', 'app-download']], ['eyebrow', 'Eyebrow'], ['title', 'Section title', 'text', true], ['description', 'Description', 'textarea'], ['category', 'Product category'], ['cta_label', 'Primary button label'], ['cta_href', 'Primary button URL', 'url'], ['secondary_label', 'Secondary button label'], ['secondary_href', 'Secondary button URL', 'url'], ['image', 'Desktop image', 'image'], ['mobile_image', 'Mobile image', 'image'], ['primary_button_image', 'Primary store button image', 'image'], ['secondary_button_image', 'Secondary store button image', 'image'], ['background', 'Background style'], ['enabled', 'Visible on website', 'checkbox']] },
  brands: { label: 'Brands', icon: '🏷️', title: 'name', fields: [['name', 'Brand name', 'text', true], ['slug', 'Slug', 'text', true], ['logo', 'Brand logo', 'image', true], ['alt', 'Logo alt text'], ['href', 'Shop URL', 'url'], ['show_on_homepage', 'Show on homepage', 'checkbox']] },
  stores: { label: 'Stores', icon: '🏬', title: 'name', fields: [['name', 'Store name', 'text', true], ['city', 'City', 'text', true], ['address', 'Address', 'textarea', true], ['image', 'Store image', 'image'], ['alt', 'Image alt text'], ['href', 'Directions / page URL', 'url'], ['phone', 'Phone'], ['email', 'Email', 'email'], ['featured', 'Featured store', 'checkbox'], ['show_on_homepage', 'Show on homepage', 'checkbox']] },
  articles: { label: 'Articles & Media', icon: '📰', title: 'title', fields: [['title', 'Article title', 'text', true], ['slug', 'Slug', 'text', true], ['excerpt', 'Excerpt', 'textarea'], ['content', 'Article content', 'textarea'], ['image', 'Article image', 'image'], ['alt', 'Image alt text'], ['category', 'Category'], ['author', 'Author'], ['published_at', 'Published date', 'date'], ['href', 'Article URL', 'url'], ['featured', 'Featured', 'checkbox'], ['show_on_homepage', 'Show on homepage', 'checkbox']] },
  faqs: { label: 'FAQs', icon: '❓', title: 'question', fields: [['question', 'Question', 'text', true], ['answer', 'Answer', 'textarea', true], ['category', 'Category']] },
  testimonials: { label: 'Testimonials', icon: '💬', title: 'customer_name', fields: [['customer_name', 'Customer name', 'text', true], ['designation', 'Designation'], ['profile_image', 'Profile image', 'image'], ['rating', 'Rating (1-5)', 'number'], ['content', 'Testimonial', 'textarea', true]] },
  navigation: { label: 'Navigation & Dropdowns', icon: '🧭', title: 'label', fields: [['label', 'Menu label', 'text', true], ['menu_type', 'Menu placement', 'menu-type', true], ['parent_id', 'Parent main menu', 'relation-navigation', true], ['destination_type', 'Menu destination', 'destination-type', true], ['page_title', 'Dynamic page title'], ['page_excerpt', 'Dynamic page intro', 'textarea'], ['page_content', 'Dynamic page content', 'textarea'], ['page_featured_image', 'Dynamic page image', 'image'], ['page_items', 'Page sections & items', 'page-items'], ['url', 'Existing / external page URL', 'url'], ['icon', 'Icon / emoji'], ['badge', 'Badge'], ['external', 'External link', 'checkbox'], ['new_tab', 'Open in new tab', 'checkbox']] },
  footer: { label: 'Footer Builder', icon: '🦶', title: 'title', fields: [['item_type', 'Item type', 'select', true, ['column', 'link', 'action', 'social', 'office', 'legal', 'meta']], ['section', 'Column / group key'], ['title', 'Title'], ['label', 'Link / office label'], ['url', 'URL', 'url'], ['icon', 'Social icon', 'select', false, ['facebook', 'instagram', 'linkedin', 'youtube']], ['address', 'Office address', 'textarea'], ['description', 'Prompt / disclaimer', 'textarea'], ['variant', 'Button variant'], ['external', 'External link', 'checkbox'], ['new_tab', 'Open in new tab', 'checkbox'], ['copyright_text', 'Copyright text']] },
  settings: { label: 'Website Settings', icon: '⚙️', title: 'website_name', fields: [['website_name', 'Website name', 'text', true], ['logo', 'Desktop header/footer logo', 'image'], ['mobile_logo', 'Mobile header logo', 'image'], ['logo_alt', 'Logo alt text'], ['favicon', 'Favicon', 'image'], ['email', 'Email', 'email'], ['phone', 'Phone'], ['whatsapp', 'WhatsApp URL', 'url'], ['address', 'Address', 'textarea'], ['google_maps_url', 'Google Maps URL', 'url'], ['support_label', 'Header support label'], ['support_url', 'Header support URL', 'url'], ['search_placeholder', 'Search placeholder'], ['wishlist_label', 'Wishlist label'], ['wishlist_url', 'Wishlist URL', 'url'], ['cart_label', 'Cart label'], ['cart_url', 'Header cart URL', 'url'], ['account_label', 'Account label'], ['account_url', 'Account/login URL', 'url'], ['social_label', 'Footer social heading'], ['facebook', 'Facebook URL', 'url'], ['instagram', 'Instagram URL', 'url'], ['linkedin', 'LinkedIn URL', 'url'], ['youtube', 'YouTube URL', 'url'], ['default_seo_title', 'Default SEO title'], ['default_seo_description', 'Default SEO description', 'textarea']] },
  seo: { label: 'SEO Settings', icon: '🔎', title: 'page_name', fields: [['page_name', 'Page name', 'text', true], ['seo_title', 'SEO title', 'text', true], ['seo_description', 'SEO description', 'textarea'], ['seo_keywords', 'SEO keywords'], ['canonical_url', 'Canonical URL', 'url'], ['og_image', 'Social share image', 'image']] },
  enquiries: { label: 'Contact Enquiries', icon: '📨', title: 'name', readOnly: true, fields: [['name', 'Name'], ['email', 'Email'], ['phone', 'Phone'], ['subject', 'Subject'], ['message', 'Message', 'textarea']] },
  media: { label: 'Media Library', icon: '🖼️', title: 'original_name', readOnly: true, fields: [] },
};

const emptyRecord = { status: 'draft', is_active: true, display_order: 0 };
const PAGE_NAMES = {
  buy: 'Buy', sell: 'Sell', partner: 'Partner', corporate: 'Corporate', franchise: 'Franchise', contact: 'Contact', support: 'Support', stores: 'Stores', minutes: 'ZOBOX Minutes', about: 'About Us',
  'buy-mobile': 'Buy Mobile', 'buy-laptop': 'Buy Laptop', 'zobiz-partner': 'ZOBIZ Partner', 'become-partner': 'Become a Partner', 'channel-partner': 'Channel Partner', 'contact-us': 'Contact Us', wholesaler: 'Wholesaler', refurbished: 'Refurbished Devices',
  'press-release': 'Press Releases', offers: 'Offers', help: 'Help', faq: 'Frequently Asked Questions', 'grade-condition': 'Grade & Condition', 'privacy-policy': 'Privacy Policy', terms: 'Terms & Conditions', 'terms-of-use': 'Terms of Use',
  'cookies-policy': 'Cookies Policy', 'shipping-policy': 'Shipping Policy', 'return-refund-policy': 'Return & Refund Policy', warranty: 'Warranty', 'warranty-policy': 'Warranty Policy',
};
const pageNameFromRecord = (record = {}) => {
  const slug = String(record.slug || '').trim().toLowerCase();
  if (PAGE_NAMES[slug]) return PAGE_NAMES[slug];
  if (slug) return slug.split('-').filter(Boolean).map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join(' ');
  return record.page_name || 'Website Page';
};
const IMAGE_GUIDANCE = {
  'banners.image': 'Recommended: 1920 × 700 px (desktop), WebP/JPG/PNG, max 5 MB',
  'banners.mobile_image': 'Recommended: 750 × 900 px (mobile), WebP/JPG/PNG, max 5 MB',
  'homepage-sections.image': 'Recommended: 1600 × 900 px, WebP/JPG/PNG, max 5 MB',
  'homepage-sections.mobile_image': 'Recommended: 750 × 900 px, WebP/JPG/PNG, max 5 MB',
  'settings.logo': 'Recommended: 420 × 120 px, transparent PNG/WebP/SVG, max 5 MB',
  'settings.mobile_logo': 'Recommended: 240 × 80 px, transparent PNG/WebP/SVG, max 5 MB',
  'settings.favicon': 'Recommended: 512 × 512 px square PNG/SVG, max 5 MB',
  'brands.logo': 'Recommended: 400 × 240 px, transparent PNG/WebP/SVG, max 5 MB',
  'pages.featured_image': 'Recommended: 1600 × 900 px (16:9), WebP/JPG/PNG, max 5 MB',
  'articles.image': 'Recommended: 1200 × 675 px (16:9), WebP/JPG/PNG, max 5 MB',
  'categories.image': 'Recommended: 800 × 800 px square, WebP/JPG/PNG, max 5 MB',
  'stores.image': 'Recommended: 1200 × 800 px (3:2), WebP/JPG/PNG, max 5 MB',
  'testimonials.profile_image': 'Recommended: 500 × 500 px square, WebP/JPG/PNG, max 5 MB',
  'pages.history_image': 'Recommended: 800 × 600 px (SVG or PNG)',
  'pages.leader_image': 'Recommended: 600 × 600 px square portrait (SVG or PNG)',
  'pages.work_image': 'Recommended: 1200 × 500 px wide banner (SVG or PNG)',
  default: 'Recommended: at least 1200 px wide, WebP/JPG/PNG/SVG, max 5 MB',
};
const navigationType = (record = {}) => record.menu_type || (record.parent_id ? 'submenu' : 'main');
const destinationType = (record = {}) => record.destination_type || (record.linked_page_id || String(record.url || '').startsWith('/content/') ? 'dynamic' : record.url ? 'custom' : 'dropdown');

const slugify = (value = '') => String(value)
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '');

const readableMessage = (value, fallback = 'Something went wrong') => {
  if (!value) return fallback;
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  if (Array.isArray(value)) {
    return value.map((item) => {
      if (typeof item === 'string') return item;
      const field = Array.isArray(item?.loc) ? item.loc.filter((part) => part !== 'body').join('.') : '';
      return [field, item?.msg || item?.message].filter(Boolean).join(': ') || JSON.stringify(item);
    }).join(' · ');
  }
  if (value.detail) return readableMessage(value.detail, fallback);
  return value.msg || value.message || fallback;
};

function CMS({ user, onLogout }) {
  const [section, setSection] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [records, setRecords] = useState([]);
  const [meta, setMeta] = useState({ page: 1, pages: 1, total: 0 });
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [editing, setEditing] = useState(null);
  const [productLoading, setProductLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [newImageUrl, setNewImageUrl] = useState('');
  const [form, setForm] = useState(emptyRecord);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [navigationOptions, setNavigationOptions] = useState([]);
  const [pageOptions, setPageOptions] = useState([]);
  const [copyBlockModal, setCopyBlockModal] = useState(null);
  const [isCopyingBlock, setIsCopyingBlock] = useState(false);
  const [editingMedia, setEditingMedia] = useState(null);
  const [mediaReplacement, setMediaReplacement] = useState(null);

  const client = useMemo(() => axios.create({ baseURL: API, headers: { Authorization: `Bearer ${localStorage.getItem('accessToken') || localStorage.getItem('token') || localStorage.getItem('access_token')}` } }), []);
  const config = resources[section];

  const [isSaving, setIsSaving] = useState(false);
  const [mobileModalTab, setMobileModalTab] = useState('edit');
  const [isPreviewExpanded, setIsPreviewExpanded] = useState(false);

  // Elementor-style Visual Page Builder state
  const [visualBuilderDoc, setVisualBuilderDoc] = useState(null);
  const [visualEditingPage, setVisualEditingPage] = useState(null);
  const [versionHistoryPage, setVersionHistoryPage] = useState(null);
  const [isConflictModalOpen, setIsConflictModalOpen] = useState(false);
  const [conflictData, setConflictData] = useState({});
  const [isSavingVisual, setIsSavingVisual] = useState(false);

  const {
    isApproved,
    canApprove,
    validationErrors,
    isValid,
    approvePreview,
    resetApproval,
  } = usePreviewApproval({
    form,
    section,
    config,
    isUploading: uploadingImage,
    isLoading: productLoading || loading,
  });

  const showMessage = (type, text) => {
    setMessage({ type, text: readableMessage(text) });
    window.setTimeout(() => setMessage(null), 3500);
  };

  const handleProductUploadImages = async (files) => {
    if (!files || files.length === 0) return;
    setUploadingImage(true);
    const newUrls = [];
    for (const file of Array.from(files)) {
      const body = new FormData();
      body.append('file', file);
      try {
        const { data } = await client.post('/cms/media/upload', body, { headers: { 'Content-Type': 'multipart/form-data' } });
        const url = resolveMediaUrl(data.url);
        newUrls.push(url);
      } catch (err) {
        showMessage('error', err.response?.data?.detail || `Upload failed for ${file.name}`);
      }
    }
    if (newUrls.length > 0) {
      setForm((curr) => {
        const currentGallery = Array.isArray(curr.gallery) ? curr.gallery : (curr.gallery ? [curr.gallery] : []);
        const updatedGallery = [...currentGallery, ...newUrls];
        const feat = curr.featured_image || updatedGallery[0] || '';
        return { ...curr, gallery: updatedGallery, featured_image: feat };
      });
      showMessage('success', `${newUrls.length} image(s) uploaded`);
    }
    setUploadingImage(false);
  };

  const handleProductAddUrl = () => {
    const url = newImageUrl.trim();
    if (!url) return;
    if (!/^https?:\/\//i.test(url) && !url.startsWith('/')) {
      showMessage('error', 'Please enter a valid image URL');
      return;
    }
    setForm((curr) => {
      const currentGallery = Array.isArray(curr.gallery) ? curr.gallery : (curr.gallery ? [curr.gallery] : []);
      const updatedGallery = [...currentGallery, url];
      const feat = curr.featured_image || url;
      return { ...curr, gallery: updatedGallery, featured_image: feat };
    });
    setNewImageUrl('');
  };

  const handleProductSetFeatured = (url) => {
    setForm((curr) => {
      const currentGallery = Array.isArray(curr.gallery) ? curr.gallery : (curr.gallery ? [curr.gallery] : []);
      const reordered = [url, ...currentGallery.filter((u) => u !== url)];
      return { ...curr, featured_image: url, gallery: reordered };
    });
    showMessage('success', 'Cover photo updated');
  };

  const handleProductRemoveImage = (url) => {
    setForm((curr) => {
      const currentGallery = (Array.isArray(curr.gallery) ? curr.gallery : []).filter((u) => u !== url);
      let nextFeatured = curr.featured_image;
      if (nextFeatured === url) {
        nextFeatured = currentGallery[0] || '';
      }
      return { ...curr, gallery: currentGallery, featured_image: nextFeatured };
    });
  };

  const updateVariantField = (variantId, field, value) => {
    setForm((curr) => ({
      ...curr,
      variants: (curr.variants || []).map((v) => (v.id === variantId ? { ...v, [field]: value } : v)),
    }));
  };

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try { setDashboard((await client.get('/cms/dashboard')).data.counts); }
    catch (error) { showMessage('error', error.response?.data?.detail || 'Could not load CMS dashboard'); }
    finally { setLoading(false); }
  }, [client]);

  const importWebsiteDefaults = async () => {
    if (!window.confirm('This will replace current CMS presentation content with the supplied website defaults. Continue?')) return;
    setLoading(true);
    try {
      const { data } = await client.post('/cms/import-website-defaults');
      showMessage('success', data.message);
      await loadDashboard();
    } catch (error) { showMessage('error', error.response?.data?.detail || 'Website import failed'); }
    finally { setLoading(false); }
  };

  const loadRecords = useCallback(async (page = 1) => {
    if (!config) return;
    setLoading(true);
    try {
      if (section === 'media') {
        const { data } = await client.get('/cms/media', { params: { page, limit: 24, search: search || undefined } });
        setRecords(data.items || []);
        setMeta(data);
        return;
      }
      const { data } = await client.get(`/cms/${section}`, { params: { page, limit: section === 'pages' ? 200 : 12, search: search || undefined, status: status || undefined } });
      setRecords(data.items); setMeta(data);
      if (section === 'products') {
        const categories = await client.get('/cms/categories', { params: { page: 1, limit: 500, sort_by: 'display_order', sort_order: 'asc' } });
        setCategoryOptions(categories.data.items || []);
      }
      if (section === 'navigation' || section === 'pages') {
        const navigation = await client.get('/cms/navigation', { params: { page: 1, limit: 100, sort_by: 'display_order', sort_order: 'asc' } });
        setNavigationOptions((navigation.data.items || []).filter((item) => navigationType(item) === 'main' && !item.parent_id && ['published', 'active'].includes(String(item.status || '').toLowerCase()) && item.is_active !== false));
      }
      if (section === 'page-blocks') {
        const pagesRes = await client.get('/cms/pages', { params: { page: 1, limit: 200, sort_by: 'title', sort_order: 'asc' } });
        setPageOptions(pagesRes.data.items || []);
      }
    } catch (error) { showMessage('error', error.response?.data?.detail || `Could not load ${config.label}`); }
    finally { setLoading(false); }
  }, [client, config, search, section, status]);

  const openVisualBuilder = async (record = null) => {
    if (!record?.id) {
      const blank = createBlankPageDocument({
        title: 'New Page',
        slug: '',
        status: 'draft',
      });
      setVisualEditingPage(null);
      setVisualBuilderDoc(blank);
      return;
    }

    setLoading(true);
    try {
      const { data: pageData } = await client.get(`/cms/pages/${record.id}`);
      let blocks = [];
      if (pageData.slug) {
        const { data: blocksData } = await client.get('/cms/page-blocks', {
          params: { page: 1, limit: 500, page_slug: pageData.slug, sort_by: 'display_order', sort_order: 'asc' },
        });
        blocks = blocksData.items || [];
      }
      const canonical = legacyToCanonical(pageData, blocks);
      setVisualEditingPage(pageData);
      setVisualBuilderDoc(canonical);
    } catch (error) {
      showMessage('error', error.response?.data?.detail || 'Could not load page for Visual Builder');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseVisualBuilder = () => {
    setVisualBuilderDoc(null);
    setVisualEditingPage(null);
  };

  const handleSaveVisualBuilder = async (firstArg, secondArg) => {
    setIsSavingVisual(true);
    try {
      const canonicalDoc = (secondArg && secondArg.sections) ? secondArg : (firstArg?.sections ? firstArg : null);
      const pagePayload = canonicalDoc ? canonicalToLegacy(canonicalDoc) : firstArg;

      let saved;
      if (visualEditingPage?.id) {
        const res = await client.put(`/cms/pages/${visualEditingPage.id}`, pagePayload);
        saved = res.data;
      } else {
        const res = await client.post('/cms/pages', pagePayload);
        saved = res.data;
      }

      showMessage('success', `Page "${saved.title || pagePayload.title}" saved successfully`);
      setVisualBuilderDoc(null);
      setVisualEditingPage(null);
      await loadRecords(meta.page);
    } catch (error) {
      showMessage('error', error.response?.data?.detail || error.message || 'Failed to save page');
      throw error;
    } finally {
      setIsSavingVisual(false);
    }
  };

  const handleMediaUpload = async (files) => {
    if (!files || files.length === 0) return;
    setLoading(true);
    let count = 0;
    for (const file of Array.from(files)) {
      const body = new FormData();
      body.append('file', file);
      try {
        await client.post('/cms/media/upload', body, { headers: { 'Content-Type': 'multipart/form-data' } });
        count++;
      } catch (err) {
        showMessage('error', err.response?.data?.detail || `Upload failed for ${file.name}`);
      }
    }
    if (count > 0) {
      showMessage('success', `${count} media file(s) uploaded successfully`);
      await loadRecords(1);
    }
    setLoading(false);
  };

  const handleMediaEdit = async (event) => {
    event.preventDefault();
    if (!editingMedia?.id) return;
    setLoading(true);
    try {
      const body = new FormData();
      if (mediaReplacement) body.append('file', mediaReplacement);
      body.append('original_name', editingMedia.original_name || editingMedia.filename || 'media');
      body.append('alt', editingMedia.alt || '');
      await client.put(`/cms/media/${editingMedia.id}`, body, { headers: { 'Content-Type': 'multipart/form-data' } });
      showMessage('success', mediaReplacement ? 'Media file replaced' : 'Media details updated');
      setEditingMedia(null);
      setMediaReplacement(null);
      await loadRecords(meta.page);
    } catch (error) {
      showMessage('error', error.response?.data?.detail || 'Media update failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (section === 'dashboard') loadDashboard(); else loadRecords(1); }, [section, loadDashboard, loadRecords]);

  const openForm = async (record = null, stagedStatus = null) => {
    resetApproval();
    setMobileModalTab('edit');
    setIsPreviewExpanded(false);
    setEditing(record);
    if (!record?.id) {
      if (section === 'navigation') {
        return setForm({ ...emptyRecord, status: stagedStatus || 'published', is_active: true, menu_type: 'main', destination_type: 'dynamic', page_items: [] });
      }
      if (section === 'homepage-sections') {
        return setForm({ ...emptyRecord, status: stagedStatus || 'published', is_active: true, enabled: true });
      }
      if (section === 'pages') {
        return setForm({ ...emptyRecord, status: stagedStatus || 'published', is_active: true, page_items: [], show_in_navbar: false, navbar_placement: 'main', navbar_label: '' });
      }
      return setForm({ ...emptyRecord, status: stagedStatus || 'published', is_active: true });
    }
    if (section === 'products') {
      setProductLoading(true);
      setForm({ ...emptyRecord, variants: [], gallery: [] });
      try {
        const { data } = await client.get(`/cms/products/${record.id}`);
        const galleryList = Array.isArray(data.gallery)
          ? data.gallery
          : (data.images && Array.isArray(data.images))
          ? data.images
          : (typeof data.gallery === 'string' && data.gallery)
          ? data.gallery.split(',').map((s) => s.trim()).filter(Boolean)
          : [];
        const featured = data.featured_image || (galleryList.length > 0 ? galleryList[0] : '');

        setForm({
          ...data,
          website_status: stagedStatus || data.website_status || 'draft',
          featured_image: featured,
          gallery: galleryList,
          regular_price: data.regular_price !== null && data.regular_price !== undefined ? data.regular_price : '',
          sale_price: data.sale_price !== null && data.sale_price !== undefined ? data.sale_price : '',
          specifications: typeof data.specifications === 'object' ? JSON.stringify(data.specifications, null, 2) : (data.specifications || '{}'),
          variants: (data.variants || []).map((v) => ({
            ...v,
            sales_grade: v.sales_grade || 'GOOD',
            sale_price: v.sale_price !== null && v.sale_price !== undefined ? v.sale_price : '',
            regular_price: v.regular_price !== null && v.regular_price !== undefined ? v.regular_price : (v.mrp || ''),
            is_available: v.is_available ?? true,
          })),
          colors: Array.isArray(data.colors) ? data.colors.join(', ') : data.colors || '',
        });
      } catch (error) {
        closeForm();
        showMessage('error', error.response?.data?.detail || 'Product could not be loaded');
      } finally {
        setProductLoading(false);
      }
      return;
    }
    const normalizedStatus = stagedStatus || String(record.status || 'draft').toLowerCase();
    const finalStatus = ['draft', 'published', 'active', 'inactive'].includes(normalizedStatus) ? normalizedStatus : 'draft';
    let nextForm = {
      ...record,
      ...(section === 'navigation' ? { menu_type: navigationType(record), destination_type: destinationType(record) } : {}),
      status: finalStatus,
      is_active: ['published', 'active'].includes(finalStatus),
    };
    if (section === 'navigation' && destinationType(record) === 'dynamic' && record.linked_page_id) {
      try {
        const { data: page } = await client.get(`/cms/pages/${record.linked_page_id}`);
        const { data: blocks } = await client.get('/cms/page-blocks', { params: { page: 1, limit: 500, page_slug: page.slug, sort_by: 'display_order', sort_order: 'asc' } });
        nextForm = { ...nextForm, page_title: page.title, page_slug: page.slug, page_excerpt: page.excerpt, page_content: page.content, page_featured_image: page.featured_image, page_items: blocks.items || [] };
      } catch (error) { showMessage('error', error.response?.data?.detail || 'Linked page could not be loaded'); }
    }
    if (section === 'pages') {
      try {
        const { data: pageDetails } = await client.get(`/cms/pages/${record.id}`);
        nextForm = {
          ...nextForm,
          page_name: pageNameFromRecord(pageDetails),
          show_in_navbar: Boolean(pageDetails.show_in_navbar),
          navbar_label: pageDetails.navbar_label || pageDetails.title || '',
          navbar_placement: pageDetails.navbar_placement || 'main',
          navbar_parent_id: pageDetails.navbar_parent_id || '',
        };
      } catch (e) {
        // ignore
      }
      if (record.slug) {
        try {
          const { data: blocks } = await client.get('/cms/page-blocks', { params: { page: 1, limit: 500, page_slug: record.slug, sort_by: 'display_order', sort_order: 'asc' } });
          nextForm = { ...nextForm, page_items: blocks.items || [] };
        } catch (error) { showMessage('error', error.response?.data?.detail || 'Page items could not be loaded'); }
      }
    }
    if (section === 'page-blocks') {
      try {
        const pagesRes = await client.get('/cms/pages', { params: { page: 1, limit: 200, sort_by: 'title', sort_order: 'asc' } });
        setPageOptions(pagesRes.data.items || []);
      } catch (e) {
        // ignore
      }
    }
    setForm(nextForm);
  };
  const closeForm = () => { setEditing(null); setForm(emptyRecord); setNewImageUrl(''); setIsPreviewExpanded(false); resetApproval(); };

  const handleOpenCopyModal = async (block) => {
    try {
      if (!pageOptions.length) {
        const { data } = await client.get('/cms/pages', { params: { page: 1, limit: 200, sort_by: 'title', sort_order: 'asc' } });
        setPageOptions(data.items || []);
      }
    } catch (e) {
      // ignore
    }
    setCopyBlockModal({ block, targetSlug: block.page_slug || '' });
  };

  const handleExecuteCopyBlock = async () => {
    if (!copyBlockModal?.block || !copyBlockModal.targetSlug) {
      showMessage('error', 'Please select or enter a target page slug');
      return;
    }
    setIsCopyingBlock(true);
    try {
      const { id, _id, created_at, updated_at, ...rest } = copyBlockModal.block;
      await client.post('/cms/page-blocks', {
        ...rest,
        page_slug: copyBlockModal.targetSlug,
      });
      showMessage('success', `Card "${copyBlockModal.block.title}" copied to /${copyBlockModal.targetSlug}`);
      setCopyBlockModal(null);
      await loadRecords(meta.page);
    } catch (err) {
      showMessage('error', err.response?.data?.detail || 'Failed to copy card');
    } finally {
      setIsCopyingBlock(false);
    }
  };

  const save = async (event) => {
    if (event) event.preventDefault();
    if (!isApproved) {
      showMessage('error', 'Please review and approve the live preview before saving changes.');
      return;
    }
    setIsSaving(true);
    try {
      let payload = { ...form };
      if (section === 'products') {
        const editable = [
          'name', 'slug', 'website_category_id', 'short_description', 'description',
          'featured_image', 'gallery', 'specifications', 'colors', 'seo_title', 'seo_description',
          'website_status', 'display_order', 'regular_price', 'sale_price'
        ];
        payload = Object.fromEntries(
          editable.filter((field) => form[field] !== undefined).map((field) => [field, form[field]])
        );
        payload.website_status = form.website_status || 'draft';
        payload.display_order = Number(form.display_order || 0);

        // Ensure gallery is a list of URLs
        const galleryArr = Array.isArray(form.gallery) ? form.gallery.filter(Boolean) : [];
        payload.gallery = galleryArr;
        payload.featured_image = form.featured_image || (galleryArr[0] || '');

        // Standalone product pricing & stock
        if (form.regular_price !== undefined && form.regular_price !== '') {
          payload.regular_price = Number(form.regular_price);
        }
        if (form.sale_price !== undefined && form.sale_price !== '') {
          payload.sale_price = Number(form.sale_price);
        }
        if (form.stock !== undefined && form.stock !== '') {
          payload.stock = Number(form.stock);
          payload.stock_quantity = Number(form.stock);
        } else if (form.stock_quantity !== undefined && form.stock_quantity !== '') {
          payload.stock = Number(form.stock_quantity);
          payload.stock_quantity = Number(form.stock_quantity);
        }

        // Variants with rate, stock, and grade
        if (Array.isArray(form.variants) && form.variants.length > 0) {
          payload.variants = form.variants.map((v) => ({
            id: v.id,
            sales_grade: v.sales_grade || 'GOOD',
            sale_price: v.sale_price !== '' && v.sale_price !== null && v.sale_price !== undefined ? Number(v.sale_price) : null,
            regular_price: v.regular_price !== '' && v.regular_price !== null && v.regular_price !== undefined ? Number(v.regular_price) : null,
            mrp: v.regular_price !== '' && v.regular_price !== null && v.regular_price !== undefined ? Number(v.regular_price) : null,
            stock_quantity: v.stock_quantity !== '' && v.stock_quantity !== null && v.stock_quantity !== undefined ? Number(v.stock_quantity) : (v.stock !== '' && v.stock !== null && v.stock !== undefined ? Number(v.stock) : 0),
            stock: v.stock_quantity !== '' && v.stock_quantity !== null && v.stock_quantity !== undefined ? Number(v.stock_quantity) : (v.stock !== '' && v.stock !== null && v.stock !== undefined ? Number(v.stock) : 0),
            is_available: Boolean(v.is_available),
          }));
        }

        try {
          payload.specifications = typeof form.specifications === 'string' ? JSON.parse(form.specifications || '{}') : (form.specifications || {});
        } catch {
          showMessage('error', 'Specifications must be valid JSON, for example {"Display": "6.1 inch"}');
          setIsSaving(false);
          return;
        }
        if (!payload.specifications || Array.isArray(payload.specifications) || typeof payload.specifications !== 'object') {
          showMessage('error', 'Specifications must be a JSON object');
          setIsSaving(false);
          return;
        }
      }
      if (section === 'pages') {
        payload.slug = String(form.slug || '').trim();
        if (!payload.slug) {
          showMessage('error', 'Page slug is required. Enter it manually.');
          setIsSaving(false);
          return;
        }
        if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(payload.slug)) {
          showMessage('error', 'Slug can use lowercase letters, numbers and single hyphens only.');
          setIsSaving(false);
          return;
        }
        payload.page_name = pageNameFromRecord({ ...form, slug: payload.slug });
        payload.show_in_navbar = Boolean(form.show_in_navbar);
        if (form.show_in_navbar) {
          payload.navbar_label = form.navbar_label || form.title || '';
          payload.navbar_placement = form.navbar_placement || 'main';
          if (form.navbar_placement === 'submenu') {
            payload.navbar_parent_id = form.navbar_parent_id || '';
          }
        }
        if (Array.isArray(form.page_items)) {
          payload.page_items = form.page_items;
        }
        const contentChanged = !editing ||
          payload.content !== editing.content ||
          payload.excerpt !== editing.excerpt ||
          payload.eyebrow !== editing.eyebrow ||
          payload.title !== editing.title ||
          payload.featured_image !== editing.featured_image ||
          JSON.stringify(payload.page_items || []) !== JSON.stringify(editing.page_items || []) ||
          !Array.isArray(payload.sections) ||
          payload.sections.length === 0;

        if (contentChanged) {
          const canonical = legacyToCanonical({ ...payload, sections: [] }, payload.page_items || []);
          payload.sections = canonical.sections;
        }
      } else if (payload.slug) {
        payload.slug = slugify(payload.slug);
      }
      if (section === 'navigation') {
        payload.menu_type = navigationType(payload);
        payload.destination_type = destinationType(payload);
        if (payload.menu_type === 'submenu' && !payload.parent_id) {
          showMessage('error', 'Choose the main menu under which this submenu should appear');
          setIsSaving(false);
          return;
        }
        if (payload.menu_type === 'submenu' && payload.destination_type === 'dropdown') {
          showMessage('error', 'A submenu must open a dynamic page or an existing URL');
          setIsSaving(false);
          return;
        }
        if (payload.destination_type === 'dynamic') {
          payload.page_title = payload.page_title || payload.label;
          payload.page_slug = slugify(payload.page_title);
        }
        if (payload.destination_type === 'custom' && !String(payload.url || '').trim()) {
          showMessage('error', 'Enter the existing or external page URL');
          setIsSaving(false);
          return;
        }
        if (payload.menu_type === 'main') delete payload.parent_id;
      }
      if (payload.status === 'published' || payload.status === 'active') {
        payload.is_active = true;
      } else if (payload.status === 'unpublished' || payload.status === 'draft' || payload.status === 'inactive') {
        payload.is_active = false;
      }
      if (editing?.id) {
        const params = editing.updated_at ? { expected_updated_at: editing.updated_at } : {};
        await client.put(`/cms/${section}/${editing.id}`, payload, { params });
      } else {
        await client.post(`/cms/${section}`, payload);
      }

      showMessage('success', `${config.label} saved`); closeForm(); loadRecords(meta.page);
    } catch (error) {
      if (error.response?.status === 409 && (error.response?.data?.detail?.code === 'CONCURRENT_MODIFICATION' || error.response?.data?.code === 'CONCURRENT_MODIFICATION')) {
        setConflictData(error.response.data.detail || error.response.data);
        setIsConflictModalOpen(true);
        setIsSaving(false);
        return;
      }
      showMessage('error', error.response?.data?.detail?.message || error.response?.data?.detail || 'Save failed');
    }
    finally { setIsSaving(false); }
  };

  const remove = async (record) => {
    if (!window.confirm('Delete this record permanently?')) return;
    try { await client.delete(`/cms/${section}/${record.id}`); showMessage('success', 'Record deleted'); loadRecords(meta.page); }
    catch (error) { showMessage('error', error.response?.data?.detail || 'Delete failed'); }
  };

  const changeStatus = async (record, nextStatus) => {
    const effectiveStatus = section === 'products' ? nextStatus : nextStatus === 'draft' ? 'unpublished' : nextStatus;
    try { await client.patch(`/cms/${section}/${record.id}/status`, null, { params: { status: effectiveStatus } }); showMessage('success', `Status changed to ${effectiveStatus}`); loadRecords(meta.page); }
    catch (error) { showMessage('error', error.response?.data?.detail || 'Status update failed'); }
  };

  const moveRecord = async (record, direction) => {
    try {
      await client.put(`/cms/${section}/${record.id}`, section === 'products' ? { display_order: Math.max(0, Number(record.display_order || 0) + direction) } : { ...record, display_order: Math.max(0, Number(record.display_order || 0) + direction) });
      await loadRecords(meta.page);
    } catch (error) { showMessage('error', error.response?.data?.detail || 'Could not change order'); }
  };

  const upload = async (field, file) => {
    if (!file) return;
    const body = new FormData(); body.append('file', file);
    try {
      const { data } = await client.post('/cms/media/upload', body, { headers: { 'Content-Type': 'multipart/form-data' } });
      const url = resolveMediaUrl(data.url);
      setForm((current) => ({ ...current, [field]: url }));
      showMessage('success', 'Image uploaded');
      return url;
    } catch (error) { showMessage('error', error.response?.data?.detail || 'Upload failed'); }
  };

  const uploadMany = async (field, files) => {
    const urls = [];
    for (const file of Array.from(files || [])) {
      const body = new FormData(); body.append('file', file);
      try {
        const { data } = await client.post('/cms/media/upload', body, { headers: { 'Content-Type': 'multipart/form-data' } });
        urls.push(resolveMediaUrl(data.url));
      } catch (error) { showMessage('error', error.response?.data?.detail || `Upload failed for ${file.name}`); }
    }
    if (urls.length) setForm((current) => ({ ...current, [field]: [current[field], ...urls].filter(Boolean).join(',') }));
  };

  const displayName = (record) => section === 'pages'
    ? pageNameFromRecord(record)
    : (record[config.title] || record.title || record.name || record.slug || record.id);
  const updatePageItem = (index, field, value) => setForm((current) => ({ ...current, page_items: (current.page_items || []).map((item, itemIndex) => itemIndex === index ? { ...item, [field]: value } : item) }));
  const uploadPageItemImage = async (index, file) => {
    if (!file) return;
    const body = new FormData(); body.append('file', file);
    try {
      const { data } = await client.post('/cms/media/upload', body, { headers: { 'Content-Type': 'multipart/form-data' } });
      updatePageItem(index, 'image', resolveMediaUrl(data.url));
      showMessage('success', 'Item image uploaded');
    } catch (error) { showMessage('error', error.response?.data?.detail || 'Item image upload failed'); }
  };
  const recordStatus = (record) => section === 'products' ? record.website_status || 'draft' : record.status;
  const recordDescription = (record) => {
    if (section === 'products') {
      return [record.sku_code, record.brand, record.category, `${record.stock ?? 0} in stock`, `${record.variants?.length || 0} variants`].filter(Boolean).join(' · ');
    }
    if (section === 'page-blocks') {
      const pageRef = record.page_slug ? `Page: /${record.page_slug}` : 'Page block';
      const blockType = record.block_type ? `[${record.block_type}]` : '';
      const dest = record.href ? `Link: ${record.href}` : (record.status_text || '');
      return [pageRef, blockType, dest].filter(Boolean).join(' · ');
    }
    if (section === 'banners') {
      const placement = record.placement ? `Placement: ${record.placement}` : 'hero';
      const group = record.group_key ? `(${record.group_key})` : '';
      const target = record.button_url ? `Link: ${record.button_url}` : '';
      return [`${placement} ${group}`.trim(), target].filter(Boolean).join(' · ') || 'Banner';
    }
    if (section === 'homepage-sections') {
      const type = record.section_type ? `Type: ${record.section_type}` : '';
      const key = record.section_key ? `Section: ${record.section_key}` : '';
      const cat = record.category ? `Category: ${record.category}` : '';
      return [type, key, cat].filter(Boolean).join(' · ') || 'Homepage Section';
    }
    if (section === 'stores') {
      const city = record.city ? `City: ${record.city}` : '';
      const addr = record.address ? record.address.replace(/\n+/g, ', ') : '';
      return [city, addr].filter(Boolean).join(' · ') || 'Store location';
    }
    if (section === 'testimonials') {
      const role = record.designation || 'Customer';
      const stars = record.rating ? `${'★'.repeat(Math.min(5, Math.max(1, Number(record.rating))))}` : '';
      return [role, stars].filter(Boolean).join(' · ');
    }
    if (section === 'pages') {
      const route = record.route || (record.slug ? `/content/${record.slug}` : '');
      const slug = record.slug ? `Slug: ${record.slug}` : '';
      const layout = record.layout ? `Layout: ${record.layout}` : '';
      const nav = record.show_in_navbar ? 'In Navbar' : '';
      return [slug, route ? `Route: ${route}` : '', layout, nav].filter(Boolean).join(' · ') || 'Website Page';
    }
    if (section === 'categories') {
      const slug = record.slug ? `/${record.slug}` : '';
      const parent = record.parent_id ? `Parent: ${record.parent_id}` : '';
      return [slug, parent].filter(Boolean).join(' · ') || 'Category';
    }
    if (section === 'brands') {
      const slug = record.slug ? `Slug: ${record.slug}` : '';
      const link = record.href ? `Link: ${record.href}` : '';
      return [slug, link].filter(Boolean).join(' · ') || 'Brand';
    }
    if (section === 'articles') {
      const cat = record.category ? `Category: ${record.category}` : '';
      const url = record.slug ? `/content/${record.slug}` : '';
      return [cat, url].filter(Boolean).join(' · ') || 'Article';
    }
    if (section === 'footer') {
      const type = record.item_type ? `Type: ${record.item_type}` : '';
      const sec = record.section ? `Group: ${record.section}` : '';
      const url = record.url ? `URL: ${record.url}` : '';
      return [type, sec, url].filter(Boolean).join(' · ') || 'Footer item';
    }
    if (section === 'faqs') {
      return record.category ? `Category: ${record.category}` : 'FAQ item';
    }
    if (section === 'navigation') {
      const parent = navigationOptions.find((item) => String(item.id) === String(record.parent_id));
      const placement = navigationType(record) === 'main' ? 'Main navbar menu' : `Submenu under ${parent?.label || 'main menu'}`;
      const destination = destinationType(record) === 'dynamic' ? `Dynamic page: ${record.page_slug || record.url}` : destinationType(record) === 'custom' ? `URL: ${record.url}` : 'Dropdown only';
      return `${placement} · ${destination}`;
    }
    if (section === 'enquiries') {
      return [record.email, record.phone].filter(Boolean).join(' · ') || 'Enquiry';
    }
    if (section === 'seo') {
      return record.page_name ? `Page: ${record.page_name}` : (record.canonical_url || 'SEO settings');
    }
    return record.slug || record.email || '';
  };

  const renderField = ([field, label, type = 'text', required = false, options = []]) => {
    if (section === 'navigation' && field === 'parent_id' && navigationType(form) !== 'submenu') return null;
    if (section === 'navigation' && field.startsWith('page_') && destinationType(form) !== 'dynamic') return null;
    if (section === 'navigation' && field === 'url' && destinationType(form) !== 'custom') return null;
    if (section === 'pages' && ['navbar_label', 'navbar_placement', 'navbar_parent_id'].includes(field) && !form.show_in_navbar) return null;
    if (section === 'pages' && field === 'navbar_parent_id' && form.navbar_placement !== 'submenu') return null;
    const update = (value) => setForm((current) => ({ ...current, [field]: value }));
    let control;
    if (type === 'readonly') {
      control = <div className="px-3 py-2 bg-gray-50 border rounded-lg text-gray-600">{form[field] ?? 'Not set'}</div>;
    } else if (type === 'website-category') {
      control = <select value={form[field] || ''} onChange={(event) => update(event.target.value)} className="w-full border rounded-lg px-3 py-2"><option value="">No website category</option>{categoryOptions.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select>;
    } else if (type === 'product-variants') {
      control = <div className="overflow-x-auto"><p className="text-xs text-gray-500 mb-2">Price and stock come from Product Management. Selling price shows the lowest priced available variant.</p><a href="/products?tab=variants" className="text-indigo-700 underline">Manage variants and pricing</a><table className="w-full text-sm mt-2"><thead><tr><th className="text-left">Variant</th><th>Grade</th><th>Price</th><th>Stock</th></tr></thead><tbody>{(form.variants || []).map((variant) => <tr key={variant.id} className="border-t"><td className="py-2">{[variant.memory, variant.color].filter(Boolean).join(' / ') || variant.zupc_code}</td><td className="text-center">{variant.sales_grade || '—'}</td><td className="text-center">{variant.sale_price ?? 'Not set'}</td><td className="text-center">{variant.stock}</td></tr>)}</tbody></table>{!form.variants?.length && <p className="text-gray-500 mt-2">No variants linked. Catalog price and stock are shown where available.</p>}</div>;
    } else if (type === 'textarea') {
      control = <textarea rows={5} required={required} value={form[field] || ''} onChange={(event) => update(event.target.value)} className="w-full border rounded-lg px-3 py-2" />;
    } else if (type === 'image' || type === 'media') {
      const guidance = type === 'media' ? 'MP4/WebM, max 25 MB' : (IMAGE_GUIDANCE[`${section}.${field}`] || IMAGE_GUIDANCE.default);
      control = <div><p className="mb-2 text-xs font-medium text-indigo-700 bg-indigo-50 border border-indigo-100 rounded-lg px-3 py-2">{guidance}</p><input type="file" accept={type === 'media' ? 'video/mp4,video/webm' : 'image/jpeg,image/png,image/webp,image/gif,image/svg+xml'} onChange={(event) => upload(field, event.target.files?.[0])} className="w-full text-sm" /><input value={form[field] || ''} onChange={(event) => update(event.target.value)} placeholder={`Upload or paste ${type === 'media' ? 'video' : 'image'} URL`} className="w-full border rounded-lg px-3 py-2 mt-2 text-xs" />{type === 'image' && form[field] && <img src={resolveMediaUrl(form[field])} alt="Current selection" className="mt-2 h-24 max-w-full object-contain rounded-lg border bg-slate-50 p-1" />}</div>;
    } else if (type === 'multi-image') {
      control = <div><input type="file" multiple accept="image/jpeg,image/png,image/webp,image/gif" onChange={(event) => uploadMany(field, event.target.files)} className="w-full text-sm" /><textarea rows={3} value={form[field] || ''} onChange={(event) => update(event.target.value)} placeholder="Uploaded image URLs (comma separated)" className="w-full border rounded-lg px-3 py-2 mt-2 text-xs" /></div>;
    } else if (type === 'checkbox') {
      control = <input type="checkbox" checked={Boolean(form[field])} onChange={(event) => update(event.target.checked)} className="h-5 w-5 accent-indigo-700" />;
    } else if (type === 'select') {
      control = <select required={required} value={form[field] || ''} onChange={(event) => update(event.target.value)} className="w-full border rounded-lg px-3 py-2"><option value="">Select</option>{options.map((option) => <option key={option} value={option}>{option}</option>)}</select>;
    } else if (type === 'page-select') {
      control = (
        <div className="space-y-2">
          <select
            required={required}
            value={form[field] || ''}
            onChange={(event) => update(event.target.value)}
            className="w-full border rounded-lg px-3 py-2 bg-white text-sm"
          >
            <option value="">Select target website page...</option>
            {pageOptions.map((p) => {
              const slug = p.slug || slugify(p.title);
              return (
                <option key={p.id || slug} value={slug}>
                  {p.title} (/{slug})
                </option>
              );
            })}
          </select>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 whitespace-nowrap">Or custom slug:</span>
            <input
              type="text"
              placeholder="e.g. buy or corporate"
              value={form[field] || ''}
              onChange={(e) => update(slugify(e.target.value))}
              className="text-xs border rounded px-2.5 py-1 flex-1 font-mono bg-slate-50 focus:bg-white"
            />
          </div>
          {form[field] && (
            <p className="text-[11px] text-indigo-700 font-medium">
              Target route: <code className="bg-indigo-50 px-1.5 py-0.5 rounded font-mono">/{form[field]}</code>
            </p>
          )}
        </div>
      );
    } else if (type === 'manual-slug') {
      control = <div><input required value={form[field] || ''} onChange={(event) => update(event.target.value)} placeholder="about-us (lowercase letters, numbers, hyphens)" pattern="[a-z0-9]+(?:-[a-z0-9]+)*" className="w-full border rounded-lg px-3 py-2 font-mono text-sm" /><p className="text-xs text-gray-500 mt-1">Manual only—changing the page title will not change this slug. Dynamic URL: /content/{form[field] || 'your-slug'}</p></div>;
    } else if (type === 'route') {
      control = <div><input type="text" placeholder="/about (or type about)" value={form[field] || ''} onChange={(event) => update(event.target.value.replace(/\s+/g, '-'))} onBlur={() => update(form[field] ? `/${String(form[field]).replace(/^\/+|\/+$/g, '')}` : '')} className="w-full border rounded-lg px-3 py-2" /><p className="text-xs text-gray-500 mt-1">Only a path is allowed. Query strings (?) and fragments (#) are not allowed.</p></div>;
    } else if (type === 'menu-type') {
      control = <div><select required value={navigationType(form)} onChange={(event) => setForm((current) => { const next = { ...current, menu_type: event.target.value }; if (event.target.value === 'main') delete next.parent_id; return next; })} className="w-full border rounded-lg px-3 py-2"><option value="main">Main navbar menu (separate)</option><option value="submenu">Submenu inside a main menu</option></select><p className="text-xs text-gray-500 mt-1">Main items appear directly in the navbar. Submenus appear inside the selected main item.</p></div>;
    } else if (type === 'destination-type') {
      control = <div><select required value={destinationType(form)} onChange={(event) => setForm((current) => ({ ...current, destination_type: event.target.value }))} className="w-full border rounded-lg px-3 py-2"><option value="dynamic">Create a dynamic CMS page</option><option value="custom">Open an existing / external URL</option>{navigationType(form) === 'main' && <option value="dropdown">Dropdown parent only (no page)</option>}</select><p className="text-xs text-gray-500 mt-1">Dynamic pages are created in Website Pages. Slug: /content/{slugify(form.page_title || form.label) || 'page-name'}</p></div>;
    } else if (type === 'page-items') {
      const items = form.page_items || [];
      control = <div className="space-y-3"><p className="text-xs text-gray-500">Add any number of content sections, cards, images, buttons or notes.</p>{items.map((item, index) => <div key={item.id || index} className="border rounded-xl p-4 bg-gray-50"><div className="flex flex-wrap items-center gap-2 mb-3"><strong className="text-sm">Item {index + 1}</strong><select value={item.block_type || 'section'} onChange={(event) => updatePageItem(index, 'block_type', event.target.value)} className="border rounded px-2 py-1 text-sm"><option value="section">Content section</option><option value="card">Card</option><option value="image">Image</option><option value="action">Button / action</option><option value="note">Note</option></select><span className="ml-auto flex gap-1"><button type="button" disabled={index === 0} onClick={() => setForm((current) => { const next = [...(current.page_items || [])]; [next[index - 1], next[index]] = [next[index], next[index - 1]]; return { ...current, page_items: next }; })} className="border rounded px-2 disabled:opacity-40">↑</button><button type="button" disabled={index === items.length - 1} onClick={() => setForm((current) => { const next = [...(current.page_items || [])]; [next[index], next[index + 1]] = [next[index + 1], next[index]]; return { ...current, page_items: next }; })} className="border rounded px-2 disabled:opacity-40">↓</button><button type="button" onClick={() => setForm((current) => ({ ...current, page_items: (current.page_items || []).filter((_, itemIndex) => itemIndex !== index) }))} className="text-red-700 border border-red-200 rounded px-2">Remove</button></span></div><div className="grid grid-cols-1 md:grid-cols-2 gap-3"><input value={item.eyebrow || ''} onChange={(event) => updatePageItem(index, 'eyebrow', event.target.value)} placeholder="Eyebrow / small heading" className="border rounded px-3 py-2" /><input value={item.title || ''} onChange={(event) => updatePageItem(index, 'title', event.target.value)} placeholder="Item title" className="border rounded px-3 py-2" /><textarea rows={3} value={item.description || ''} onChange={(event) => updatePageItem(index, 'description', event.target.value)} placeholder="Description / content" className="border rounded px-3 py-2 md:col-span-2" /><div><input type="file" accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml" onChange={(event) => uploadPageItemImage(index, event.target.files?.[0])} className="w-full text-xs" /><input value={item.image || ''} onChange={(event) => updatePageItem(index, 'image', event.target.value)} placeholder="Image URL" className="w-full border rounded px-3 py-2 mt-1" /></div><input value={item.alt || ''} onChange={(event) => updatePageItem(index, 'alt', event.target.value)} placeholder="Image alt text" className="border rounded px-3 py-2" /><input value={item.href || ''} onChange={(event) => updatePageItem(index, 'href', event.target.value)} placeholder="Link URL" className="border rounded px-3 py-2" /><input value={item.cta_label || ''} onChange={(event) => updatePageItem(index, 'cta_label', event.target.value)} placeholder="Button label" className="border rounded px-3 py-2" /></div></div>)}<button type="button" onClick={() => setForm((current) => ({ ...current, page_items: [...(current.page_items || []), { block_type: 'section', title: '', description: '' }] }))} className="px-4 py-2 border border-indigo-700 text-indigo-700 rounded-lg">+ Add page item</button></div>;
    } else if (type === 'relation-navigation') {
      control = <select required={required} value={form[field] || ''} onChange={(event) => update(event.target.value)} className="w-full border rounded-lg px-3 py-2"><option value="">Select a main menu</option>{navigationOptions.filter((item) => item.id !== editing?.id).map((item) => <option key={item.id} value={item.id}>{item.label}{item.status !== 'published' ? ` (${item.status})` : ''}</option>)}</select>;
    } else if (field === 'category_id' && categoryOptions.length) {
      control = <select required={required} value={form[field] || ''} onChange={(event) => update(event.target.value)} className="w-full border rounded-lg px-3 py-2"><option value="">Select category</option>{categoryOptions.map((category) => <option key={category.id} value={category.id}>{category.name}</option>)}</select>;
    } else {
      control = <input type={type === 'url' ? 'text' : type} placeholder={type === 'url' ? '/internal-page or https://example.com' : undefined} required={required} value={form[field] ?? ''} onChange={(event) => update(type === 'number' ? Number(event.target.value) : event.target.value)} className="w-full border rounded-lg px-3 py-2" />;
    }
    return <label key={field} className={['textarea', 'page-items', 'product-variants'].includes(type) ? 'md:col-span-2' : ''}><span className="block text-sm font-medium mb-1">{label}{required && ' *'}</span>{control}</label>;
  };

  const activeGroup = useMemo(() => {
    const found = IA_GROUPS.find((g) => g.sections.includes(section));
    return found ? found.id : 'dashboard';
  }, [section]);

  const currentGroupObj = useMemo(() => {
    return IA_GROUPS.find((g) => g.id === activeGroup) || IA_GROUPS[0];
  }, [activeGroup]);

  const selectGroup = (groupId) => {
    if (groupId === 'page-builder') { openVisualBuilder(null); return; }
    setSearch('');
    setStatus('');
    const grp = IA_GROUPS.find((g) => g.id === groupId);
    if (!grp) return;
    if (grp.sections.includes(section)) return;
    setSection(grp.sections[0]);
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6" data-testid="cms-page">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold" style={{ color: '#2b3380' }}>Website CMS</h1>
            <p className="text-gray-600">Manage the complete ZOBOX storefront from the ERP</p>
          </div>
          <div className="flex gap-2">
            {section !== 'dashboard' && section !== 'products' && section !== 'media' && section !== 'pages' && !config?.readOnly && (
              <button onClick={() => openForm({})} className="px-4 py-2 rounded-lg border border-indigo-700 text-indigo-700 font-semibold hover:bg-indigo-50 transition-colors">
                + Add {config?.label}
              </button>
            )}
          </div>
        </div>

        {section === 'products' && <p className="mb-4 text-sm text-gray-600">Products added in <a href="/products" className="underline text-indigo-700">Product Management</a> appear here automatically. Publish or hide them on the website and edit their content here. Refresh to load the latest inventory.</p>}
        {message && <div className={`mb-4 p-3 rounded-lg ${message.type === 'error' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>{message.text}</div>}

        {/* Tier 1: Information Architecture Main Groups */}
        <div className="flex gap-2 overflow-x-auto pb-2 mb-2">
          {IA_GROUPS.map((group) => {
            const isGroupActive = activeGroup === group.id;
            return (
              <button
                key={group.id}
                onClick={() => selectGroup(group.id)}
                className={`whitespace-nowrap px-4 py-2 rounded-xl text-sm font-semibold transition-all flex items-center gap-2 cursor-pointer ${
                  isGroupActive
                    ? 'text-white shadow-md'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                }`}
                style={isGroupActive ? { backgroundColor: '#2b3380' } : {}}
              >
                <span>{group.icon}</span>
                <span>{group.label}</span>
              </button>
            );
          })}
        </div>

        {/* Tier 2: Sub-resources inside Active Group */}
        {currentGroupObj.sections.length > 1 && (
          <div className="flex items-center gap-2 overflow-x-auto pb-2 mb-5 bg-slate-100/90 p-2 rounded-xl border border-slate-200">
            <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wider px-2">Sub-resources:</span>
            {currentGroupObj.sections.map((secKey) => {
              const secCfg = resources[secKey];
              if (!secCfg) return null;
              const isSecActive = section === secKey;
              return (
                <button
                  key={secKey}
                  onClick={() => { setSearch(''); setStatus(''); setSection(secKey); }}
                  className={`whitespace-nowrap px-3 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5 cursor-pointer ${
                    isSecActive
                      ? 'bg-white text-indigo-900 shadow-sm border border-slate-300 font-bold'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-white/60'
                  }`}
                >
                  <span>{secCfg.icon}</span>
                  <span>{secCfg.label}</span>
                </button>
              );
            })}
          </div>
        )}

        {section === 'dashboard' ? (
          loading ? <Loading /> : <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {Object.entries(resources).filter(([k]) => k !== 'media').map(([key, item]) => { const count = dashboard?.[key.replace('-', '_')] || {}; return <button key={key} onClick={() => setSection(key)} className="bg-white text-left p-5 rounded-xl shadow hover:shadow-lg transition"><div className="text-3xl mb-3">{item.icon}</div><div className="text-2xl font-bold text-gray-900">{count.total ?? 0}</div><div className="font-medium text-gray-700">{item.label}</div><div className="text-xs text-gray-500 mt-2">{count.published ?? 0} published · {count.draft ?? 0} drafts</div></button>; })}
          </div>
        ) : section === 'media' ? (
          <div className="bg-white rounded-xl shadow overflow-hidden p-6 space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-4 border-b pb-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <span>🖼️</span> Media Library
                </h2>
                <p className="text-xs text-gray-500 mt-1">Upload, preview, replace, rename and delete storefront media.</p>
                <p className="text-xs font-medium text-indigo-700 mt-1">Images: max 5 MB · Videos: max 25 MB · Exact recommended dimensions are shown beside every CMS upload field.</p>
              </div>
              <div>
                <label className="cursor-pointer px-4 py-2 rounded-xl text-white font-semibold text-sm shadow flex items-center gap-2 transition-all hover:opacity-95" style={{ backgroundColor: '#2b3380' }}>
                  <span>+ Upload Media</span>
                  <input
                    type="file"
                    multiple
                    accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml,video/mp4,video/webm"
                    onChange={(e) => handleMediaUpload(e.target.files)}
                    className="hidden"
                  />
                </label>
              </div>
            </div>

            <div className="flex gap-3">
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && loadRecords(1)}
                placeholder="Search media by filename..."
                className="border rounded-lg px-3 py-2 flex-1 text-sm"
              />
              <button onClick={() => loadRecords(1)} className="px-4 py-2 border rounded-lg text-sm">Search</button>
            </div>

            {loading ? <Loading /> : records.length === 0 ? (
              <div className="py-16 text-center border-2 border-dashed border-gray-200 rounded-2xl bg-gray-50">
                <p className="text-base font-semibold text-gray-700">No media assets found</p>
                <p className="text-xs text-gray-400 mt-1">Upload photos, icons, banners, or video files to see them here.</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {records.map((item) => {
                  const isVideo = item.content_type?.startsWith('video/') || /\.(mp4|webm)$/i.test(item.url || '');
                  const fullUrl = resolveMediaUrl(item.url);
                  return (
                    <div key={item.id} className="group relative border rounded-xl overflow-hidden bg-white shadow-sm hover:shadow-md transition-all flex flex-col">
                      <div className="h-32 bg-slate-100 flex items-center justify-center p-2 relative overflow-hidden">
                        {isVideo ? (
                          <video src={fullUrl} className="w-full h-full object-contain" muted controls preload="metadata" />
                        ) : (
                          <img src={fullUrl} alt={item.original_name || ''} className="w-full h-full object-contain" />
                        )}
                        <div className="absolute inset-0 bg-black/40 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              navigator.clipboard.writeText(fullUrl);
                              showMessage('success', 'URL copied to clipboard');
                            }}
                            className="p-1.5 bg-white rounded-lg text-xs font-semibold text-gray-800 shadow hover:bg-gray-100"
                            title="Copy URL"
                          >
                            📋
                          </button>
                          <button
                            type="button"
                            onClick={() => { setEditingMedia({ ...item }); setMediaReplacement(null); }}
                            className="p-1.5 bg-indigo-700 rounded-lg text-xs font-semibold text-white shadow hover:bg-indigo-800"
                            title="Edit or replace media"
                          >
                            ✏️
                          </button>
                          <button
                            type="button"
                            onClick={async () => {
                              if (!window.confirm('Delete this media file?')) return;
                              try {
                                await client.delete(`/cms/media/${item.id}`);
                                showMessage('success', 'Media deleted');
                                loadRecords(meta.page);
                              } catch (err) {
                                showMessage('error', err.response?.data?.detail || 'Delete failed');
                              }
                            }}
                            className="p-1.5 bg-red-600 rounded-lg text-xs font-semibold text-white shadow hover:bg-red-700"
                            title="Delete media"
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                      <div className="p-2.5 flex-1 flex flex-col justify-between text-xs bg-white border-t">
                        <div className="truncate font-semibold text-gray-800" title={item.original_name || item.filename}>
                          {item.original_name || item.filename}
                        </div>
                        <div className="text-[11px] text-gray-400 mt-1 flex justify-between">
                          <span>{item.size ? `${(item.size / 1024).toFixed(0)} KB` : ''}</span>
                          <span>{item.created_at ? new Date(item.created_at).toLocaleDateString() : ''}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="p-4 border-t flex justify-between items-center text-sm">
              <span>{meta.total} assets</span>
              <div className="flex gap-2">
                <button disabled={meta.page <= 1} onClick={() => loadRecords(meta.page - 1)} className="border rounded px-3 py-1 disabled:opacity-40">Previous</button>
                <span className="px-2 py-1">{meta.page} / {meta.pages}</span>
                <button disabled={meta.page >= meta.pages} onClick={() => loadRecords(meta.page + 1)} className="border rounded px-3 py-1 disabled:opacity-40">Next</button>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow overflow-hidden">
            <div className="p-4 border-b flex flex-wrap gap-3">
              <input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && loadRecords(1)} placeholder={`Search ${config.label.toLowerCase()}...`} className="border rounded-lg px-3 py-2 flex-1 min-w-[220px]" />
              <select value={status} onChange={(e) => setStatus(e.target.value)} className="border rounded-lg px-3 py-2"><option value="">All statuses</option>{(section === 'products' ? ['draft', 'published', 'hidden'] : ['draft', 'published', 'unpublished', 'active', 'inactive', 'new', 'resolved']).map((value) => <option key={value}>{value}</option>)}</select>
              <button onClick={() => loadRecords(1)} className="px-4 py-2 border rounded-lg">Search</button>
            </div>
            {loading ? <Loading /> : records.length === 0 ? <div className="p-12 text-center text-gray-500">No records found. {section === 'products' ? 'Add products in Product Management to get started.' : config.readOnly ? 'New website submissions will appear here.' : 'Create the first one to publish website content.'}</div> : (
              section === 'pages' ? <CMSPagesTable records={records} onEdit={openVisualBuilder} onDelete={remove} onToggle={changeStatus} /> : <div className="overflow-x-auto"><table className="w-full"><thead className="bg-gray-50 text-left text-xs uppercase text-gray-500"><tr><th className="p-4">{section === 'pages' ? 'Page name / slug' : 'Name'}</th>{section === 'products' && <th className="p-4">Rate (MRP)</th>}<th className="p-4">Status</th><th className="p-4">Order</th><th className="p-4">Updated</th><th className="p-4 text-right">Actions</th></tr></thead><tbody>
                {records.map((record) => <tr key={record.id} className="border-t hover:bg-gray-50/70 transition-colors"><td className="p-4"><div className="flex items-center gap-3">{section === 'products' && (record.featured_image ? <img src={record.featured_image} alt="" className="w-12 h-12 object-contain rounded border bg-white p-1 flex-shrink-0" /> : <div className="w-12 h-12 bg-gray-100 rounded border flex items-center justify-center text-[10px] text-gray-400 flex-shrink-0">No Photo</div>)}<div><div className="font-semibold text-gray-900 flex items-center gap-2"><span>{displayName(record)}</span>{section === 'pages' && record.show_in_navbar && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-100 text-blue-800 border border-blue-200">🧭 In Navbar</span>}</div><div className="text-xs text-gray-500">{recordDescription(record)}</div></div></div></td>{section === 'products' && <td className="p-4 whitespace-nowrap"><div className="font-bold text-green-700 text-sm">{record.sale_price ? `₹${Number(record.sale_price).toLocaleString()}` : '—'}</div>{record.regular_price && <div className="text-xs text-gray-400 line-through">₹{Number(record.regular_price).toLocaleString()}</div>}</td>}<td className="p-4">{section === 'products' ? <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${record.website_status === 'published' ? 'bg-green-100 text-green-800' : record.website_status === 'hidden' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`}>{record.website_status === 'published' ? '● Live on Website' : record.website_status === 'hidden' ? '○ Hidden' : '◐ Draft (Offline)'}</span> : <span className={`px-2 py-1 rounded-full text-xs ${['published', 'active', 'resolved'].includes(recordStatus(record)) ? 'bg-green-100 text-green-800' : recordStatus(record) === 'new' ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'}`}>{recordStatus(record)}</span>}</td><td className="p-4"><div className="flex items-center gap-2"><span>{record.display_order ?? 0}</span>{!config.readOnly && <><button title="Move up" onClick={() => moveRecord(record, -1)} className="border rounded px-2 text-xs">↑</button><button title="Move down" onClick={() => moveRecord(record, 1)} className="border rounded px-2 text-xs">↓</button></>}</div></td><td className="p-4 text-sm text-gray-500 whitespace-nowrap">{record.updated_at ? new Date(record.updated_at).toLocaleDateString() : '—'}</td><td className="p-4"><div className="flex justify-end items-center gap-2">{section === 'pages' && (<><button onClick={() => openVisualBuilder(record)} className="px-2.5 py-1 rounded text-xs font-semibold text-white bg-indigo-700 hover:bg-indigo-800 shadow-sm flex items-center gap-1 transition-all" title="Open in Visual Page Builder"><span>🎨</span> Visual Builder</button><button type="button" onClick={() => setVersionHistoryPage(record)} className="px-2.5 py-1 rounded text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-300 flex items-center gap-1 transition-all" title="View Version History & Rollback"><span>🕒</span> History</button></>)}{section === 'page-blocks' && <button type="button" onClick={() => handleOpenCopyModal(record)} className="px-2.5 py-1 rounded text-xs font-semibold text-amber-800 bg-amber-50 hover:bg-amber-100 border border-amber-200 shadow-xs flex items-center gap-1 transition-all" title="Copy this card to another page"><span>📋</span> Copy to Page...</button>}{!config.readOnly && <><button onClick={() => openForm(record)} className="px-2.5 py-1 rounded text-xs font-semibold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200">Edit</button><button onClick={() => { if (recordStatus(record) === 'published') { changeStatus(record, section === 'products' ? 'hidden' : 'draft'); } else { changeStatus(record, 'published'); } }} className={`px-2.5 py-1 rounded text-xs font-semibold border ${recordStatus(record) === 'published' ? 'text-red-700 bg-red-50 hover:bg-red-100 border-red-200' : 'text-green-700 bg-green-50 hover:bg-green-100 border-green-200'}`}>{recordStatus(record) === 'published' ? (section === 'products' ? 'Hide' : 'Unpublish') : (section === 'products' ? 'Go Live' : 'Publish')}</button></>} {config.readOnly && recordStatus(record) === 'new' && <button onClick={() => changeStatus(record, 'resolved')} className="text-green-700 font-semibold text-xs">Resolve</button>}{section !== 'products' && <button onClick={() => remove(record)} className="text-red-700 text-xs font-semibold ml-1">Delete</button>}</div></td></tr>)}
              </tbody></table></div>
            )}
            <div className="p-4 border-t flex justify-between items-center text-sm"><span>{meta.total} records</span><div className="flex gap-2"><button disabled={meta.page <= 1} onClick={() => loadRecords(meta.page - 1)} className="border rounded px-3 py-1 disabled:opacity-40">Previous</button><span className="px-2 py-1">{meta.page} / {meta.pages}</span><button disabled={meta.page >= meta.pages} onClick={() => loadRecords(meta.page + 1)} className="border rounded px-3 py-1 disabled:opacity-40">Next</button></div></div>
          </div>
        )}
      </div>

      {editingMedia && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
          <form onSubmit={handleMediaEdit} className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden border border-slate-200">
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <div><h2 className="font-bold text-slate-900">Edit Media</h2><p className="text-xs text-slate-500">Rename the asset or replace its file without deleting the record first.</p></div>
              <button type="button" onClick={() => { setEditingMedia(null); setMediaReplacement(null); }} className="text-slate-500 text-xl">✕</button>
            </div>
            <div className="p-6 space-y-4">
              <div className="h-44 rounded-xl border bg-slate-50 flex items-center justify-center overflow-hidden">
                {editingMedia.content_type?.startsWith('video/') ? <video src={resolveMediaUrl(editingMedia.url)} controls className="w-full h-full object-contain" /> : <img src={resolveMediaUrl(editingMedia.url)} alt={editingMedia.alt || editingMedia.original_name || ''} className="w-full h-full object-contain" />}
              </div>
              <label className="block"><span className="block text-sm font-medium mb-1">File name</span><input value={editingMedia.original_name || ''} onChange={(event) => setEditingMedia((current) => ({ ...current, original_name: event.target.value }))} className="w-full border rounded-lg px-3 py-2" /></label>
              <label className="block"><span className="block text-sm font-medium mb-1">Alt text</span><input value={editingMedia.alt || ''} onChange={(event) => setEditingMedia((current) => ({ ...current, alt: event.target.value }))} className="w-full border rounded-lg px-3 py-2" /></label>
              <label className="block"><span className="block text-sm font-medium mb-1">Replace file (optional)</span><p className="text-xs text-indigo-700 mb-2">Images max 5 MB · Videos max 25 MB. Use the dimensions shown on the destination upload field.</p><input type="file" accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml,video/mp4,video/webm" onChange={(event) => setMediaReplacement(event.target.files?.[0] || null)} className="w-full text-sm" /></label>
            </div>
            <div className="px-6 py-4 bg-slate-50 border-t flex justify-end gap-2"><button type="button" onClick={() => { setEditingMedia(null); setMediaReplacement(null); }} className="px-4 py-2 border rounded-lg">Cancel</button><button type="submit" disabled={loading} className="px-5 py-2 bg-[#2b3380] text-white font-semibold rounded-lg disabled:opacity-50">{loading ? 'Saving...' : 'Save Media'}</button></div>
          </form>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-2 sm:p-4 md:p-6 backdrop-blur-sm">
          <form
            onSubmit={save}
            className={`bg-white rounded-2xl shadow-2xl w-full ${
              isPreviewExpanded ? 'max-w-[98vw] h-[96vh]' : 'max-w-7xl h-[92vh]'
            } flex flex-col overflow-hidden border border-slate-200 transition-all duration-200`}
          >
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-slate-50 via-white to-slate-50 flex justify-between items-center flex-shrink-0">
              <div className="flex items-center gap-3">
                <span className="text-2xl p-2 bg-indigo-50 rounded-xl border border-indigo-100">{config.icon}</span>
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-lg md:text-xl font-bold text-gray-900">
                      {section === 'products' ? `Manage Website Product: ${form.name || editing.name || 'Product'}` : `${editing.id ? 'Edit' : 'Add'} ${config.label}`}
                    </h2>
                    <span className={`px-2 py-0.5 rounded text-[11px] font-bold uppercase tracking-wider ${
                      (form.status === 'published' || form.website_status === 'published')
                        ? 'bg-emerald-100 text-emerald-800 border border-emerald-300'
                        : 'bg-amber-100 text-amber-800 border border-amber-300'
                    }`}>
                      {(form.status === 'published' || form.website_status === 'published') ? '● Published' : '◐ Draft'}
                    </span>
                    {section === 'pages' && editing?.id && (
                      <button
                        type="button"
                        onClick={() => setVersionHistoryPage(editing)}
                        className="px-2.5 py-0.5 text-xs font-semibold rounded-md border border-slate-300 hover:bg-slate-100 text-slate-700 flex items-center gap-1 transition"
                        title="View Version History & Rollback"
                      >
                        <span>🕒</span> History
                      </button>
                    )}
                  </div>
                  {section === 'products' ? (
                    <div className="flex flex-wrap items-center gap-2 text-xs text-gray-600 mt-1">
                      <span className="bg-gray-100 px-2 py-0.5 rounded font-mono border text-gray-700">SKU: {form.sku_code || 'N/A'}</span>
                      <span>Brand: <strong>{form.brand || 'N/A'}</strong></span>
                      <span>Category: <strong>{form.category || 'N/A'}</strong></span>
                      <span>Live Stock: <strong className="text-indigo-900">{form.stock ?? 0} units</strong></span>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500 mt-0.5">
                      Review changes in the real-time preview before approving and saving.
                    </p>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2">
                {/* Mobile Tab Switcher */}
                <div className="flex lg:hidden bg-slate-100 p-1 rounded-lg border border-slate-200 text-xs font-semibold">
                  <button
                    type="button"
                    onClick={() => setMobileModalTab('edit')}
                    className={`px-3 py-1.5 rounded-md transition-all ${
                      mobileModalTab === 'edit'
                        ? 'bg-white text-indigo-900 shadow-sm font-bold'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    ✏️ Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => setMobileModalTab('preview')}
                    className={`px-3 py-1.5 rounded-md transition-all flex items-center gap-1 ${
                      mobileModalTab === 'preview'
                        ? 'bg-white text-indigo-900 shadow-sm font-bold'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    👁️ Preview
                    {isApproved && <span className="w-2 h-2 rounded-full bg-emerald-500" />}
                  </button>
                </div>

                <button
                  type="button"
                  onClick={closeForm}
                  className="w-9 h-9 flex items-center justify-center rounded-xl text-gray-400 hover:text-gray-700 hover:bg-gray-100 text-xl font-bold transition-colors ml-2"
                  title="Close editor"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Modal Body: Split Screen Grid */}
            <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-12 overflow-hidden">
              {/* Left Column: Form Fields */}
              <div
                className={`h-full overflow-y-auto p-5 md:p-6 space-y-6 bg-white border-r border-gray-200 ${
                  isPreviewExpanded
                    ? 'hidden'
                    : mobileModalTab === 'preview'
                    ? 'hidden lg:block lg:col-span-5'
                    : 'block col-span-1 lg:col-span-5'
                }`}
              >
                {section === 'pages' && <div className="p-3 bg-indigo-50 border border-indigo-100 rounded-xl text-xs font-medium text-indigo-800">Page/section images: recommended 1200 × 675 px (16:9), WebP/JPG/PNG/SVG, max 5 MB. Hero images work best at 1600 × 900 px.</div>}
                {/* Validation Banner if errors exist */}
                {validationErrors.length > 0 && (
                  <div className="p-3.5 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-900 space-y-1">
                    <div className="font-bold flex items-center gap-1.5 text-amber-800">
                      <span>⚠️ Required Fields Needed for Approval:</span>
                    </div>
                    <ul className="list-disc list-inside space-y-0.5 text-amber-700">
                      {validationErrors.map((err, idx) => (
                        <li key={idx}>{err.message}</li>
                      ))}
                    </ul>
                  </div>
                )}
              {productLoading ? (
                <div className="py-16 text-center">
                  <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2" style={{ borderColor: '#2b3380' }} />
                  <p className="mt-3 text-sm text-gray-500">Loading product details...</p>
                </div>
              ) : section === 'products' ? (
                <>
                  {/* 1. Website Live & Display Control */}
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50/70 border border-blue-200/80 rounded-xl p-4 flex flex-wrap items-center justify-between gap-4">
                    <div>
                      <span className="text-xs font-bold uppercase tracking-wider text-blue-950">Website Storefront Visibility (Live Control)</span>
                      <p className="text-xs text-blue-800 mt-0.5">Control whether customers can browse and purchase this product on the website.</p>
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                      <div className="flex bg-white rounded-lg p-1 border shadow-sm">
                        {[
                          { id: 'published', label: '🟢 Live (Published)', activeClass: 'bg-green-600 text-white shadow-sm' },
                          { id: 'draft', label: '🟡 Draft (Offline)', activeClass: 'bg-amber-500 text-white shadow-sm' },
                          { id: 'hidden', label: '🔴 Hidden', activeClass: 'bg-red-600 text-white shadow-sm' },
                        ].map((opt) => (
                          <button
                            key={opt.id}
                            type="button"
                            onClick={() => setForm((curr) => ({ ...curr, website_status: opt.id }))}
                            className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${
                              (form.website_status || 'draft') === opt.id ? opt.activeClass : 'text-gray-600 hover:text-gray-900'
                            }`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                      <div className="flex items-center gap-1.5 text-xs bg-white px-2.5 py-1 rounded-lg border">
                        <span className="text-gray-500">Order:</span>
                        <input
                          type="number"
                          min="0"
                          value={form.display_order ?? 0}
                          onChange={(e) => setForm((curr) => ({ ...curr, display_order: Number(e.target.value) }))}
                          className="w-14 border rounded px-1.5 py-0.5 text-center font-bold"
                        />
                      </div>
                    </div>
                  </div>

                  {/* 2. Visual Product Images Manager */}
                  <div className="border border-gray-200 rounded-xl p-5 bg-white shadow-sm space-y-4">
                    <div className="flex flex-wrap items-center justify-between gap-2 border-b pb-3">
                      <div>
                        <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
                          <span>📸 Product Images &amp; Media</span>
                          <span className="text-xs bg-indigo-100 text-indigo-800 px-2 py-0.5 rounded-full font-semibold">
                            {(form.gallery || []).length} Photos
                          </span>
                        </h3>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Upload clear product photos. Recommended: <strong>1200 × 1200 px square</strong>, max 5 MB. Click <strong>Set as Cover</strong> to make any photo the main website image.
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <label className="cursor-pointer px-4 py-2 bg-[#2b3380] hover:bg-[#1e2669] text-white rounded-lg text-xs font-semibold flex items-center gap-1.5 shadow-sm transition-colors">
                          <span>+ Upload Photos</span>
                          <input
                            type="file"
                            multiple
                            accept="image/jpeg,image/png,image/webp,image/gif"
                            onChange={(e) => handleProductUploadImages(e.target.files)}
                            className="hidden"
                          />
                        </label>
                      </div>
                    </div>

                    {uploadingImage && (
                      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700 flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-blue-700 border-t-transparent rounded-full animate-spin"></div>
                        <span>Uploading images, please wait...</span>
                      </div>
                    )}

                    {/* Image Thumbnails Grid */}
                    {(form.gallery || []).length === 0 ? (
                      <div className="py-8 text-center border-2 border-dashed border-gray-200 rounded-xl bg-gray-50">
                        <p className="text-sm font-medium text-gray-600">No images uploaded yet</p>
                        <p className="text-xs text-gray-400 mt-1">Upload product photos or paste an image URL below</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                        {(form.gallery || []).map((imgUrl, index) => {
                          const isCover = (form.featured_image && form.featured_image === imgUrl) || (!form.featured_image && index === 0);
                          return (
                            <div
                              key={index}
                              className={`relative group rounded-xl border-2 p-2 bg-gray-50 flex flex-col items-center justify-between transition-all ${
                                isCover ? 'border-[#2b3380] bg-indigo-50/40 shadow-sm' : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <div className="w-full h-28 flex items-center justify-center overflow-hidden rounded-lg bg-white mb-2">
                                <img src={imgUrl} alt="" className="w-full h-full object-contain" />
                              </div>
                              {isCover ? (
                                <span className="w-full text-center text-[10px] font-bold bg-[#2b3380] text-white py-1 rounded">
                                  ★ Main Cover Photo
                                </span>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => handleProductSetFeatured(imgUrl)}
                                  className="w-full text-center text-[11px] font-medium text-indigo-700 border border-indigo-200 bg-white hover:bg-indigo-50 py-1 rounded transition-colors"
                                >
                                  Set as Cover
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={() => handleProductRemoveImage(imgUrl)}
                                title="Remove photo"
                                className="absolute -top-2 -right-2 w-6 h-6 bg-red-600 hover:bg-red-700 text-white rounded-full flex items-center justify-center text-xs font-bold shadow transition-transform hover:scale-110"
                              >
                                ✕
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Paste URL row */}
                    <div className="flex gap-2 pt-2">
                      <input
                        type="url"
                        value={newImageUrl}
                        onChange={(e) => setNewImageUrl(e.target.value)}
                        placeholder="Or paste an image URL here (https://...)..."
                        className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-xs focus:ring-2 focus:ring-[#2b3380] focus:border-transparent"
                      />
                      <button
                        type="button"
                        onClick={handleProductAddUrl}
                        className="px-3 py-1.5 border border-gray-300 hover:bg-gray-100 rounded-lg text-xs font-semibold text-gray-700 whitespace-nowrap"
                      >
                        + Add URL
                      </button>
                    </div>
                  </div>

                  {/* 3. Rates & Grading Control */}
                  <div className="border border-gray-200 rounded-xl p-5 bg-white shadow-sm space-y-4">
                    <div>
                      <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
                        <span>💰 Website Rates &amp; Sales Grading Control</span>
                      </h3>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {form.variants && form.variants.length > 0
                          ? 'Set the selling rate, catalog MRP, sales grade, and live availability for each variant below.'
                          : 'Set the customer selling rate and MRP for this product.'}
                      </p>
                    </div>

                    {form.variants && form.variants.length > 0 ? (
                      <div className="overflow-x-auto border border-gray-200 rounded-lg">
                        <table className="w-full text-left text-xs">
                          <thead className="bg-gray-50 uppercase text-gray-500 font-semibold border-b">
                            <tr>
                              <th className="p-3">Variant (Specs / ZUPC)</th>
                              <th className="p-3">Sales Grade</th>
                              <th className="p-3">Website Selling Rate (₹)</th>
                              <th className="p-3">Catalog MRP (₹)</th>
                              <th className="p-3 text-center">Live on Website</th>
                              <th className="p-3 text-center">Stock</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {form.variants.map((v) => (
                              <tr key={v.id} className="hover:bg-gray-50/80 transition-colors">
                                <td className="p-3">
                                  <div className="font-semibold text-gray-900 text-sm">
                                    {[v.memory || v.storage, v.color].filter(Boolean).join(' · ') || v.zupc_code}
                                  </div>
                                  <div className="text-[11px] text-gray-400 font-mono">{v.zupc_code}</div>
                                </td>
                                <td className="p-3">
                                  <select
                                    value={v.sales_grade || 'GOOD'}
                                    onChange={(e) => updateVariantField(v.id, 'sales_grade', e.target.value)}
                                    className="border border-gray-300 rounded px-2.5 py-1 text-xs font-semibold bg-white"
                                  >
                                    {['OKAY', 'FAIR', 'GOOD', 'SUPERB', 'EXCELLENT'].map((grade) => (
                                      <option key={grade} value={grade}>{grade}</option>
                                    ))}
                                  </select>
                                </td>
                                <td className="p-3">
                                  <div className="relative rounded-md shadow-sm w-36">
                                    <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none text-gray-500 font-bold">
                                      ₹
                                    </div>
                                    <input
                                      type="number"
                                      min="0"
                                      value={v.sale_price}
                                      onChange={(e) => updateVariantField(v.id, 'sale_price', e.target.value)}
                                      placeholder="Selling Rate"
                                      className="block w-full pl-6 pr-2 py-1 text-xs font-bold text-green-800 border border-gray-300 rounded focus:ring-green-500 focus:border-green-500"
                                    />
                                  </div>
                                </td>
                                <td className="p-3">
                                  <div className="relative rounded-md shadow-sm w-36">
                                    <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none text-gray-400">
                                      ₹
                                    </div>
                                    <input
                                      type="number"
                                      min="0"
                                      value={v.regular_price}
                                      onChange={(e) => updateVariantField(v.id, 'regular_price', e.target.value)}
                                      placeholder="Catalog MRP"
                                      className="block w-full pl-6 pr-2 py-1 text-xs text-gray-700 border border-gray-300 rounded focus:ring-indigo-500 focus:border-indigo-500"
                                    />
                                  </div>
                                </td>
                                <td className="p-3 text-center">
                                  <input
                                    type="checkbox"
                                    checked={Boolean(v.is_available)}
                                    onChange={(e) => updateVariantField(v.id, 'is_available', e.target.checked)}
                                    className="w-4 h-4 accent-green-600 rounded cursor-pointer"
                                  />
                                </td>
                                <td className="p-3 text-center">
                                  <input
                                    type="number"
                                    min="0"
                                    value={v.stock ?? v.stock_quantity ?? 0}
                                    onChange={(e) => updateVariantField(v.id, 'stock_quantity', e.target.value)}
                                    placeholder="Stock"
                                    className="w-20 text-center py-1 text-xs font-bold text-indigo-900 border border-gray-300 rounded focus:ring-indigo-500 focus:border-indigo-500 mx-auto"
                                  />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 bg-gray-50 p-4 rounded-lg">
                        <div>
                          <label className="block text-xs font-semibold text-gray-700 mb-1">
                            Website Selling Rate (₹) *
                          </label>
                          <div className="relative rounded-md shadow-sm">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-500 font-bold">
                              ₹
                            </div>
                            <input
                              type="number"
                              min="0"
                              value={form.sale_price}
                              onChange={(e) => setForm((curr) => ({ ...curr, sale_price: e.target.value }))}
                              placeholder="e.g. 1499"
                              className="block w-full pl-8 pr-3 py-2 text-sm font-bold text-green-800 border border-gray-300 rounded-lg focus:ring-green-500 focus:border-green-500"
                            />
                          </div>
                          <p className="text-[11px] text-gray-500 mt-1">Selling price on website.</p>
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-700 mb-1">
                            Catalog MRP (₹)
                          </label>
                          <div className="relative rounded-md shadow-sm">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                              ₹
                            </div>
                            <input
                              type="number"
                              min="0"
                              value={form.regular_price}
                              onChange={(e) => setForm((curr) => ({ ...curr, regular_price: e.target.value }))}
                              placeholder="e.g. 2999"
                              className="block w-full pl-8 pr-3 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                            />
                          </div>
                          <p className="text-[11px] text-gray-500 mt-1">Crossed-out reference MRP.</p>
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-700 mb-1">
                            Available Stock (Units) *
                          </label>
                          <div className="relative rounded-md shadow-sm">
                            <input
                              type="number"
                              min="0"
                              value={form.stock ?? form.stock_quantity ?? 0}
                              onChange={(e) => setForm((curr) => ({ ...curr, stock: Number(e.target.value), stock_quantity: Number(e.target.value) }))}
                              placeholder="e.g. 15"
                              className="block w-full px-3 py-2 text-sm font-bold text-indigo-900 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                            />
                          </div>
                          <p className="text-[11px] text-gray-500 mt-1">Stock available for purchase.</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* 4. Product Details & Content */}
                  <div className="border border-gray-200 rounded-xl p-5 bg-white shadow-sm space-y-4">
                    <h3 className="text-base font-bold text-gray-900 border-b pb-2">
                      📝 Website Content &amp; Descriptions
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1">Product Title *</label>
                        <input
                          type="text"
                          required
                          value={form.name || ''}
                          onChange={(e) => setForm((curr) => ({ ...curr, name: e.target.value }))}
                          className="w-full border rounded-lg px-3 py-2 text-sm font-medium"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1">Website Category</label>
                        <select
                          value={form.website_category_id || ''}
                          onChange={(e) => setForm((curr) => ({ ...curr, website_category_id: e.target.value }))}
                          className="w-full border rounded-lg px-3 py-2 text-sm"
                        >
                          <option value="">No website category</option>
                          {categoryOptions.map((c) => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-xs font-semibold text-gray-700 mb-1">Short Description / Highlights</label>
                        <textarea
                          rows={2}
                          value={form.short_description || ''}
                          onChange={(e) => setForm((curr) => ({ ...curr, short_description: e.target.value }))}
                          placeholder="Brief 1-2 sentence highlight for catalog cards..."
                          className="w-full border rounded-lg px-3 py-2 text-xs"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-xs font-semibold text-gray-700 mb-1">Full Description</label>
                        <textarea
                          rows={4}
                          value={form.description || ''}
                          onChange={(e) => setForm((curr) => ({ ...curr, description: e.target.value }))}
                          placeholder="Full product overview for the product page..."
                          className="w-full border rounded-lg px-3 py-2 text-xs"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1">Colors (Comma separated)</label>
                        <input
                          type="text"
                          value={form.colors || ''}
                          onChange={(e) => setForm((curr) => ({ ...curr, colors: e.target.value }))}
                          placeholder="e.g. Space Gray, Silver, Pacific Blue"
                          className="w-full border rounded-lg px-3 py-2 text-xs"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1">SEO Page Title</label>
                        <input
                          type="text"
                          value={form.seo_title || ''}
                          onChange={(e) => setForm((curr) => ({ ...curr, seo_title: e.target.value }))}
                          placeholder="Custom SEO Title"
                          className="w-full border rounded-lg px-3 py-2 text-xs"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-xs font-semibold text-gray-700 mb-1">SEO Description</label>
                        <textarea
                          rows={2}
                          value={form.seo_description || ''}
                          onChange={(e) => setForm((curr) => ({ ...curr, seo_description: e.target.value }))}
                          placeholder="Meta description for search engines..."
                          className="w-full border rounded-lg px-3 py-2 text-xs"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-xs font-semibold text-gray-700 mb-1">
                          Specifications (JSON Object)
                        </label>
                        <textarea
                          rows={4}
                          value={form.specifications || ''}
                          onChange={(e) => setForm((curr) => ({ ...curr, specifications: e.target.value }))}
                          className="w-full font-mono text-xs border rounded-lg px-3 py-2 bg-gray-50"
                        />
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                /* Standard dynamic form for other resources */
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {config.fields.map(renderField)}
                  <label>
                    <span className="block text-sm font-medium mb-1">Status</span>
                    <select
                      value={form.status || 'draft'}
                      onChange={(e) => setForm({ ...form, status: e.target.value })}
                      className="w-full border rounded-lg px-3 py-2"
                    >
                      {['draft', 'published', 'active', 'inactive'].map((val) => (
                        <option key={val}>{val}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    <span className="block text-sm font-medium mb-1">Display order</span>
                    <input
                      type="number"
                      min="0"
                      value={form.display_order ?? 0}
                      onChange={(e) => setForm({ ...form, display_order: Number(e.target.value) })}
                      className="w-full border rounded-lg px-3 py-2"
                    />
                  </label>
                </div>
              )}
              </div>

              {/* Right Column: Live Preview Panel */}
              <div
                className={`h-full overflow-hidden flex flex-col bg-slate-50 ${
                  isPreviewExpanded
                    ? 'col-span-1 lg:col-span-12'
                    : mobileModalTab === 'edit'
                    ? 'hidden lg:flex lg:col-span-7'
                    : 'flex col-span-1 lg:col-span-7'
                }`}
              >
                <CMSPreviewPanel
                  section={section}
                  form={form}
                  categoryOptions={categoryOptions}
                  navigationOptions={navigationOptions}
                  isExpanded={isPreviewExpanded}
                  onToggleExpand={() => setIsPreviewExpanded(!isPreviewExpanded)}
                />
              </div>
            </div>

            {/* Modal Footer: Preview Approval Guard & Save Changes */}
            <div className="px-6 py-3.5 border-t border-gray-200 bg-gray-50 flex flex-col sm:flex-row items-center justify-between gap-3 flex-shrink-0">
              {/* Left: Approval Status Indicator */}
              <div className="flex items-center gap-2">
                {isApproved ? (
                  <div className="flex items-center gap-2 text-xs text-emerald-800 bg-emerald-100/80 border border-emerald-300 px-3.5 py-1.5 rounded-xl font-medium shadow-sm">
                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-600 animate-pulse" />
                    <span><strong>Preview Approved!</strong> Content is verified against live preview.</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-xs text-amber-800 bg-amber-100/70 border border-amber-300 px-3.5 py-1.5 rounded-xl font-medium shadow-sm">
                    <span className="w-2.5 h-2.5 rounded-full bg-amber-500" />
                    <span>
                      <strong>Approval Required:</strong> Review live preview and click &ldquo;Approve Preview&rdquo; to unlock Save.
                    </span>
                  </div>
                )}
              </div>

              {/* Right: Actions */}
              <div className="flex items-center gap-3 w-full sm:w-auto justify-end">
                <button
                  type="button"
                  onClick={closeForm}
                  className="px-4 py-2 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  Cancel
                </button>

                {!isApproved ? (
                  <button
                    type="button"
                    disabled={!canApprove}
                    onClick={() => {
                      const ok = approvePreview();
                      if (!ok && validationErrors.length > 0) {
                        showMessage('error', validationErrors[0].message);
                      }
                    }}
                    className={`px-5 py-2 rounded-xl text-sm font-bold shadow-sm transition-all flex items-center gap-2 ${
                      canApprove
                        ? 'bg-emerald-600 hover:bg-emerald-700 text-white cursor-pointer shadow-emerald-600/25 active:scale-[0.98]'
                        : 'bg-gray-200 text-gray-400 border border-gray-300 cursor-not-allowed'
                    }`}
                    title={canApprove ? 'Approve current preview and unlock Save Changes' : 'Fill all required fields to approve preview'}
                  >
                    <span>👁️</span>
                    <span>Approve Preview — Enable Save</span>
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={resetApproval}
                    className="px-4 py-2 rounded-xl text-sm font-semibold bg-emerald-50 text-emerald-800 border border-emerald-300 hover:bg-emerald-100 transition-colors flex items-center gap-1.5 cursor-pointer"
                    title="Click to reset approval if you need to edit further"
                  >
                    <span>✓ Approved</span>
                    <span className="text-[11px] text-emerald-600 underline ml-1">(Reset)</span>
                  </button>
                )}

                {/* CRITICAL REQUIREMENT: Save Changes is strictly HIDDEN until isApproved is true */}
                {isApproved && (
                  <button
                    type="submit"
                    disabled={isSaving || productLoading || uploadingImage}
                    className="px-6 py-2 text-white font-bold rounded-xl text-sm shadow-md hover:shadow-lg transition-all flex items-center gap-2 disabled:opacity-50 cursor-pointer active:scale-[0.98]"
                    style={{ backgroundColor: '#2b3380' }}
                  >
                    {isSaving ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>Saving...</span>
                      </>
                    ) : (
                      <>
                        <span>💾</span>
                        <span>Save Changes</span>
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </form>
        </div>
      )}

      {/* Copy Card to Another Page Modal */}
      {copyBlockModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden border border-slate-200">
            <div className="px-6 py-4 bg-gradient-to-r from-amber-500 to-[#2b3380] text-white flex justify-between items-center">
              <div className="flex items-center gap-2.5">
                <span className="text-xl">📋</span>
                <h3 className="font-bold text-base">Copy Card to Another Page</h3>
              </div>
              <button
                type="button"
                onClick={() => setCopyBlockModal(null)}
                className="text-white/80 hover:text-white text-lg font-bold cursor-pointer"
              >
                ✕
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-3.5 space-y-1">
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Selected Card</div>
                <div className="font-bold text-slate-900 text-sm">{copyBlockModal.block.title || 'Untitled Card'}</div>
                {copyBlockModal.block.eyebrow && (
                  <div className="text-xs text-indigo-600 font-medium">{copyBlockModal.block.eyebrow}</div>
                )}
                <div className="text-xs text-slate-500">
                  Current Page: <span className="font-mono font-semibold text-slate-700">/{copyBlockModal.block.page_slug || 'unassigned'}</span>
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-700">
                  Target Destination Page <span className="text-red-500">*</span>
                </label>
                <select
                  value={copyBlockModal.targetSlug || ''}
                  onChange={(e) => setCopyBlockModal((prev) => ({ ...prev, targetSlug: e.target.value }))}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Select target website page...</option>
                  {pageOptions.map((p) => {
                    const slug = p.slug || slugify(p.title);
                    return (
                      <option key={p.id || slug} value={slug}>
                        {p.title} (/{slug})
                      </option>
                    );
                  })}
                </select>

                <div className="flex items-center gap-2 pt-1">
                  <span className="text-xs text-slate-500 whitespace-nowrap">Or custom slug:</span>
                  <input
                    type="text"
                    placeholder="e.g. buy, corporate, faq"
                    value={copyBlockModal.targetSlug || ''}
                    onChange={(e) => setCopyBlockModal((prev) => ({ ...prev, targetSlug: slugify(e.target.value) }))}
                    className="text-xs border rounded-lg px-2.5 py-1 flex-1 font-mono bg-slate-50 focus:bg-white"
                  />
                </div>
                {copyBlockModal.targetSlug && (
                  <p className="text-[11px] text-indigo-700 font-medium">
                    Card will be duplicated to: <code className="bg-indigo-50 px-1.5 py-0.5 rounded font-mono">/{copyBlockModal.targetSlug}</code>
                  </p>
                )}
              </div>

              <div className="pt-3 flex justify-end gap-2 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setCopyBlockModal(null)}
                  disabled={isCopyingBlock}
                  className="px-4 py-2 border border-slate-200 text-slate-700 rounded-lg text-sm font-semibold hover:bg-slate-50 cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleExecuteCopyBlock}
                  disabled={isCopyingBlock || !copyBlockModal.targetSlug}
                  className="px-5 py-2 bg-[#2b3380] hover:bg-[#202766] text-white rounded-lg text-sm font-bold shadow-sm disabled:opacity-50 flex items-center gap-2 transition-all cursor-pointer"
                >
                  {isCopyingBlock ? (
                    <>
                      <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Duplicating...</span>
                    </>
                  ) : (
                    <>
                      <span>📋</span>
                      <span>Duplicate to Page</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Version History Modal */}
      {versionHistoryPage && (
        <CMSVersionHistory
          pageId={versionHistoryPage.id}
          pageName={displayName(versionHistoryPage)}
          client={client}
          onRollback={(restoredDoc) => {
            setVersionHistoryPage(null);
            showMessage('success', `Version restored successfully for "${displayName(restoredDoc)}"`);
            loadRecords(meta.page);
            if (editing?.id === restoredDoc.id) {
              openForm(restoredDoc);
            }
          }}
          onClose={() => setVersionHistoryPage(null)}
        />
      )}

      {/* Concurrent Edit Conflict Modal */}
      {isConflictModalOpen && (
        <CMSConflictModal
          isOpen={isConflictModalOpen}
          conflictData={conflictData}
          onReloadServerVersion={(serverDoc) => {
            setIsConflictModalOpen(false);
            setConflictData({});
            openForm(serverDoc);
            showMessage('info', 'Loaded latest version from server.');
          }}
          onOverwrite={async (currentUpdatedAt) => {
            setIsConflictModalOpen(false);
            if (editing) {
              editing.updated_at = currentUpdatedAt;
            }
            save();
          }}
          onClose={() => {
            setIsConflictModalOpen(false);
            setConflictData({});
          }}
        />
      )}

      {/* Elementor-style Visual Page Builder Fullscreen Workspace */}
      {visualBuilderDoc && (
        <PageBuilderEditor
          initialDocument={visualBuilderDoc}
          onSave={handleSaveVisualBuilder}
          onClose={handleCloseVisualBuilder}
          client={client}
          isSaving={isSavingVisual}
        />
      )}
    </Layout>
  );
}

function Loading() { return <div className="p-12 text-center"><div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2" style={{ borderColor: '#2b3380' }} /><p className="mt-3 text-gray-500">Loading live data…</p></div>; }

export default CMS;
